// ترجمة الواجهة — عربيّ/إنجليزيّ.
//
// ═══ لماذا المفتاح هو النصّ العربيّ نفسه ═══
// في الواجهة **٨٤٠٠ نصّ عربيّ في ١٢١ ملفاً**، مكتوبة داخل الكود مباشرة. ونظام
// مفاتيح تقليديّ (`nav.tracking`) يفرض اختراع ثمانية آلاف مفتاح وتغيير كل موضع
// دفعةً واحدة — أسبوعٌ من الجراحة، ونصفُه المنجَز يعرض `nav.tracking` على شاشة
// العميل حين يُنسى مفتاح.
//
// فالمفتاح هنا هو النصّ العربيّ:  t("التتبع المباشر")
//   • الترجمة تُضاف تدريجياً، صفحةً صفحة، بلا لمس البقيّة.
//   • وما لم يُترجَم بعد **يظهر عربيّاً سليماً** لا مفتاحاً مكسوراً — فالنصف
//     المنجَز صالحٌ للاستعمال، وهذا شرطُ أن يُنجَز على مراحل أصلاً.
//   • ومن يقرأ الكود يرى النصّ كما هو، فلا يحتاج فتح ملفّ القاموس ليفهم سطراً.
//
// ═══ الاتجاه ═══
// الإنجليزية تقلب اتجاه الصفحة إلى LTR (`dir` على <html>). والتنسيقات المكتوبة
// بـ`mr-`/`ml-` لا تنقلب معها؛ تُعالَج صفحةً صفحة مع ترجمتها بتحويلها إلى
// `ms-`/`me-` المنطقيّتين.

import { EN_PAGES } from "./i18n-pages";

export type Lang = "ar" | "en";

export const LANGS: { code: Lang; label: string; native: string }[] = [
  { code: "ar", label: "Arabic", native: "العربية" },
  { code: "en", label: "English", native: "English" },
];

/** القاموس: النصّ العربيّ ← الإنجليزيّ. الغائب يعود عربيّاً كما هو. */
export const EN: Record<string, string> = {
  // ── التنقّل ── مستخرَجة من `app-layout.tsx` لا مُخمَّنة: خمّنتُ أوّل مرّة
  // «المحادثات» و«العملاء» و«لوحة التحكم» وهي في الكود «صندوق الوارد» و«جهات
  // الاتصال» و«لوحة الأداء» — فبقيت عربيةً على الشاشة. القاعدة: تُنسَخ الأسماء
  // من المصدر (`grep -o 'label: "[^"]*"'`) لا تُكتب من الذاكرة.
  "العمل اليومي": "Daily Work",
  "صندوق الوارد": "Inbox",
  "جهات الاتصال": "Contacts",
  "لوحة الأداء": "Dashboard",
  "التأجير": "Rentals",
  "الحجوزات": "Bookings",
  "تقويم الليموزين": "Limo Calendar",
  "العقود": "Contracts",
  "السيارات": "Vehicles",
  "التتبع المباشر": "Live Tracking",
  "الصيانة": "Maintenance",
  "مُستبدَل برابط أحدث": "Replaced by a newer link",
  "لا يوجد مبلغ مستحق على هذا العقد في Drive8 — أدخل المبلغ يدوياً فقط إن كان هناك مستحق فعلي": "Nothing is due on this contract in Drive8 — enter an amount manually only if something is actually owed",
  "المبلغ يختلف عن متبقّي العقد": "Amount differs from the contract's remaining balance",
  "سيُنشأ رابط دفع:": "A payment link will be created:",
  "المستفيد": "Payer",
  "بدون عقد": "No contract",
  "سجل توثيق الصيانة": "Maintenance Work Log",
  "نهاية العقد": "Contract end",
  "كل ما يوثّقه مسؤول الصيانة من التطبيق — الصور مختومة بالوقت والموقع واسم الموظف، والموقع مطابق بجهاز التتبع": "Everything the maintenance staff logs from the app — photos stamped with time, location and employee name, location matched against the GPS tracker",
  "كل الأنواع": "All types", "البعيدة عن السيارة فقط": "Far from car only",
  "بحث: لوحة، وصف، محل، موظف": "Search: plate, description, shop, staff",
  "عدد العمليات": "Entries", "مجموع المصاريف": "Total spent", "بعيدة عن السيارة": "Far from car",
  "لا عمليات في هذه الفترة": "No entries in this period", "فاتورة": "Invoice",
  "عند السيارة": "At the car", "بعيد عن السيارة": "Far from car", "السيارة بلا تتبع": "Car has no tracker",
  "شراء — بدون مطابقة": "Purchase — not matched", "موقع الموظف": "Staff location", "موقع السيارة": "Car location",
  "تصليح": "Repair", "تبديل تاير": "Tire change", "تبديل بطارية": "Battery change", "تغيير زيت وفلتر": "Oil & filter",
  "فحص": "Inspection", "غسيل وتنظيف": "Wash & clean", "شراء قطع وأغراض": "Parts & supplies purchase", "أخرى": "Other", "متر": "m",
  "الإدارة المالية": "Finance",
  "روابط الدفع": "Payment Links",
  "ربحية السيارات": "Vehicle Profitability",
  "التقارير": "Reports",
  "فواتير الأجهزة": "Device Invoices",
  "التقرير المفصل": "Detailed Report",
  // «معاملات الشركة» بندٌ أضفتُه في هذه الجلسة فأسقط `verify:i18n`. والأربعة
  // معه كانت ناقصةً قبلها — والحارس الأحمر لا يمسك الخطأ التالي، فأُكملت.
  "معاملات الشركة": "Company Errands",
  "أوراق الشركة": "Company Documents",
  "الأصول": "Assets",
  "اعتماد سندات القبض": "Receipt approval",
  "التسليم والاستلام": "Handover & return",
  "قرار الأسطول": "Fleet exit decision",
  "مخزون الزيوت والفلاتر": "Oils & Filters Stock",
  "مكالمات البدالة": "PBX Calls",
  "العقود (صرف وقبض)": "Contracts (Payments)",
  "تقرير القبض والصرف": "Receipts & Expenses Report",
  "كشف التحصيل": "Collection Sheet",
  "عمولات الموظفين": "Staff Commissions",
  "الموظفين": "Staff",
  "الحضور والرواتب": "Attendance & Payroll",
  "تسجيل حضوري": "My Check-in",
  "إدارة الفريق": "Team",
  "بيانات الموظفين (ERP)": "Employees (ERP)",
  "التسويق والمحتوى": "Marketing",
  "الحملات": "Campaigns",
  "القوالب": "Templates",
  "النظام": "System",
  "الإعدادات": "Settings",
  "مكتبة الوسائط": "Media Library",
  "التكاملات": "Integrations",
  "الاشتراك والباقة": "Subscription & Plan",
  "قاعدة المعرفة": "Knowledge Base",
  "استهلاك الذكاء الاصطناعي": "AI Usage",
  "مستوى الخدمة (SLA)": "Service Level (SLA)",
  "سجل النشاط": "Activity Log",
  "لوحة الإدارة": "Admin Console",

  // ── الشريط الجانبيّ ──────────────────────────────────────────────────────
  "بحث سريع...": "Quick search…",
  "تطبيقات جسر": "Jisr apps",
  "لا توجد نتائج": "No results",
  "مسح البحث": "Clear search",
  "القائمة": "Menu",
  "توسيع القائمة": "Expand menu",
  "تصغير القائمة": "Collapse menu",
  "مدير": "Admin",
  "مشرف": "Supervisor",
  "وكيل": "Agent",

  // ── عامّ ─────────────────────────────────────────────────────────────────
  "بحث": "Search",
  "بحث…": "Search…",
  "حفظ": "Save",
  "إلغاء": "Cancel",
  "حذف": "Delete",
  "دفعٌ لا يطابق الرابط — يحتاج مراجعة": "Payment does not match the link — needs review",
  "بيانات المراجعة غير متاحة حالياً — غياب شارة «يحتاج مراجعة» لا يعني أن الدفعات سليمة.": "Review data is unavailable right now — a missing “needs review” badge does not mean the payments are fine.",
  "تعذّر الحذف": "Couldn't delete",
  "تعديل": "Edit",
  "إضافة": "Add",
  "إغلاق": "Close",
  "تحديث": "Refresh",
  "الكل": "All",
  "نعم": "Yes",
  "لا": "No",
  "من": "From",
  "إلى": "To",
  "اليوم": "Today",
  "أمس": "Yesterday",
  "جارٍ التحميل…": "Loading…",
  "لا توجد بيانات": "No data",
  "خطأ": "Error",
  "تم": "Done",
  "تسجيل الخروج": "Sign out",
  "اللغة": "Language",

  // ── التتبّع المباشر ───────────────────────────────────────────────────────
  "خطأ في التحميل:": "Loading error:",
  "رجوع": "Back",
  "الغرض من المشاركة": "Sharing purpose",
  "مدة صلاحية الرابط": "Link validity",
  "· يتوقّف الرابط تلقائياً بعدها": "· the link expires automatically",
  "البيانات المعروضة للمستلم": "Data shown to the recipient",
  "دائماً": "Always",
  "سيُنشأ رابط لـ": "A link will be created for",
  "· الغرض:": "· Purpose:",
  "· المدة:": "· Duration:",
  "بدون بيانات السائق والهاتف": "without driver name or phone",
  "تأكيد إنشاء الرابط": "Create link",
  "مشاركة عبر واتساب": "Share on WhatsApp",
  "فتح المعاينة": "Open preview",
  "يمكنك تعطيل الوصول في أي وقت دون التأثير على روابط أخرى.": "You can revoke access at any time without affecting other links.",
  "مشاركة تسجيل الرحلة": "Share trip recording",
  "الفترة الزمنية": "Time range",
  "تعذّر جلب السجل": "Could not load the history",
  "لا قراءات في هذه الفترة": "No readings in this period",
  "اللوحة:": "Plate:",
  "اتصال بالسائق": "Call driver",
  "مشاركة الرحلة": "Share trip",
  "مشاركة مباشرة": "Share live",
  "لا توجد بيانات تسجيل للفترة المحددة": "No recorded data for the selected period",
  "مخصّص": "Custom",
  "عرض": "Show",
  "🚨 حد": "🚨 Limit",
  "من الماضي": "in the past",
  "عقد": "Contract",
  "ينتهي": "ends",
  "لوحة": "Plate",
  "وقت الجهاز خاطئ": "Device clock is wrong",
  "🪫 بطارية الجهاز": "🪫 Device battery",
  "لا توجد نتائج مطابقة": "No matching results",
  "تعديل بيانات جهاز التتبع": "Edit tracker details",
  "اسم الجهاز": "Device name",
  "رقم اللوحة": "Plate number",
  "ربط بسيارة": "Link to a vehicle",
  "— غير مربوط —": "— not linked —",
  "حد السرعة الخاص (كم/س) — اتركه فارغاً ليتبع المجموعة/الحد العام": "Custom speed limit (km/h) — leave empty to follow the group/global limit",
  "حسّاس الحرارة": "Temperature sensor",
  "حدّ أعلى (°م)": "Max (°C)",
  "حدّ أدنى (°م)": "Min (°C)",
  "بعد (دقيقة)": "After (minutes)",
  "📡 اتصال الجهاز": "📡 Device connection",
  "الشريحة": "SIM",
  "⬆ تحديث متوفر": "⬆ Update available",
  "🔑 مؤجَّرة حالياً": "🔑 Currently rented",
  "الجهاز يعيد إرسال بيانات قديمة — الموقع والسرعة من رحلة سابقة، وليسا وضع السيارة الآن": "The device is resending old data — the location and speed are from an earlier trip, not the vehicle's current state",
  "مشاركة": "Share",
  "اتجاهات": "Directions",
  "التفاصيل": "Details",
  "روابط التتبع": "Tracking links",
  "جاري التحميل…": "Loading…",
  "لا توجد روابط بعد": "No links yet",
  "LIVE مباشر · ١٥ث": "LIVE · 15s",
  "تتحرّك الآن": "Moving now",
  "ازدحام": "Congestion",
  "عرض الكل": "Show all",
  "🚨 السرعة": "🚨 Speed",
  "جاري تحميل الخريطة...": "Loading map…",
  "هذه حدود المنطقة — احفظها أو ألغِ وارسم يدوياً": "These are the zone bounds — save them, or cancel and draw manually",
  "لا توجد أجهزة GPS متصلة حالياً": "No GPS devices connected right now",
  "إضافة منطقة محظورة": "Add restricted zone",
  "اسم المنطقة": "Zone name",
  "وقت التنبيه": "Alert time",
  "ابحث عن منطقة بالاسم (اختياري)": "Search for a place by name (optional)",
  "✏️ رسم يدوي على الخريطة": "✏️ Draw manually on the map",
  "رمز QR لرابط التتبع": "QR code for the tracking link",
  "تكبير": "Zoom in",
  "تصغير": "Zoom out",
  "سجل الحرارة": "Temperature history",
  "ارتفاع عابر": "Brief rise",
  "صعدت الحرارة": "Temperature rose",
  "نزلت الحرارة": "Temperature dropped",
  "رجعت للنطاق": "Back in range",
  "قعدت": "Lasted",
  "لون الخط": "Line color",
  "عابر": "Brief",
  "عرض كل الرحلات": "Show all trips",
  "رحلات فيها تجاوز حرارة": "Trips with temperature breaches",
  "لا تجاوز حرارة في هذه الرحلات": "No temperature breaches in these trips",
  "لا قراءات حرارة في هذه الفترة": "No temperature readings in this period",
  "أدنى": "Min",
  "داخل النطاق طول الرحلة": "In range the whole trip",
  "شهادة سلسلة التبريد للفترة": "Cold-chain certificate for this period",
  "شهادة تبريد هذه الرحلة": "Cold-chain certificate for this trip",
  "تعذّر إصدار الشهادة": "Could not issue the certificate",
  "جارٍ الإصدار…": "Issuing…",
  "العودة إلى التتبع المباشر": "Back to live tracking",
  "المقاطع الأسرع من هذا الحد تُرسم حمراء": "Segments faster than this limit are drawn in red",
  "الحالة الآنية": "Current status",
  "حالة المركبة": "Vehicle status",
  "ملخص الفترة المعروضة": "Summary for the shown period",
  "السائق": "Driver",
  "اتصال": "Call",
  "واتساب": "WhatsApp",

  "توقيت سجلات الجهاز متأخر بساعات — الجهاز يعيد إرسال رحلات قديمة\n                  خزّنها أثناء انقطاعه، أو ساعته غير مضبوطة. الموقع والسرعة المعروضان": "The device clock is hours behind — it is resending old trips stored while offline, or its clock is wrong. The location and speed shown are",
  "وقد لا يمثلان مكان السيارة الآن. تصحّح نفسها عادةً\n                  بعد اكتمال إرسال السجلات المخزنة؛ إن استمرت أعد تشغيل الجهاز وافحص\n                  هوائي GPS والشريحة.": "and may not reflect where the vehicle is now. This usually corrects itself once the stored records finish uploading; if it persists, restart the device and check the GPS antenna and SIM.",
  "ابحث: سيارة، لوحة، عميل، عقد، هاتف": "Search: vehicle, plate, customer, contract, phone",
  "الجهاز متصل لكنه لا يلتقط إشارة أقمار GPS — الموقع المعروض هو آخر موقع معروف وقد تكون السيارة تتحرك فعلياً. تأكد من سماء مكشوفة وافحص هوائي GPS": "The device is online but has no GPS satellite fix — the position shown is the last known one and the vehicle may actually be moving. Check for open sky and inspect the GPS antenna",
  "توقيت سجلات الجهاز متأخر بساعات عن وقت استلامها — الجهاز يعيد إرسال رحلات قديمة خزّنها وهو مفصول، أو ساعته غير مضبوطة. الموقع والسرعة المعروضان من الماضي وقد لا يمثلان مكان السيارة الآن": "The device timestamps are hours behind when we received them — it is resending old trips stored while disconnected, or its clock is wrong. The location and speed shown are from the past and may not reflect where the vehicle is now",
  "مثال: 13/19841": "e.g. 13/19841",
  "مثال: 80": "e.g. 80",
  "درجة موثوقية اتصال الجهاز — تُحدَّث يومياً": "Device connection reliability — updated daily",
  "اتصال بالعميل": "Call the customer",
  "واتساب العميل": "WhatsApp the customer",
  "الاتجاهات إلى السيارة": "Directions to the vehicle",
  "التفاصيل الكاملة": "Full details",
  "تمركز على كل السيارات": "Fit all vehicles",
  "تنبيهات السرعة: السجل والإعدادات والمجموعات": "Speed alerts: history, settings and groups",
  "مثال: منطقة المطار": "e.g. Airport area",
  "مثال: مطار الكويت": "e.g. Kuwait Airport",

  "آخر تحديث": "Last update",
  "الأسطول": "Fleet",
  "فوري": "Immediate",
  "دقيقة": "min",
  "سيصل التنبيه فور دخول السيارة المنطقة": "The alert is sent the moment the vehicle enters the zone",
  "سيصل التنبيه بعد بقاء السيارة": "The alert is sent after the vehicle stays",
  "دقائق داخل المنطقة": "minutes inside the zone",
  "🚨 تنبيهات السرعة": "🚨 Speed alerts",
  "لا توجد تجاوزات بالفترة المحددة ✅": "No violations in the selected period ✅",
  "📍 الموقع": "📍 Location",
  "درجة «خطير» فوق الحد بـ": "Marked “severe” when above the limit by",
  "أولوية الحد: حد السيارة ← حد مجموعتها ← الحد العام. حرّر حد سيارة واحدة من زر «تعديل» في بطاقتها.": "Limit priority: vehicle limit ← its group limit ← global limit. Edit a single vehicle's limit from the “Edit” button on its card.",
  "اسم المجموعة": "Group name",
  "مثال: سيارات الحد 80": "e.g. 80 km/h vehicles",
  "🔍 بحث بالاسم أو اللوحة…": "🔍 Search by name or plate…",
  "✕ إزالة المعروض": "✕ Remove shown",
  "الإشعارات": "Notifications",
  "قراءة الكل": "Mark all read",
  "مسح جميع الإشعارات": "Clear all notifications",
  "مسح الكل": "Clear all",
  "لا توجد إشعارات": "No notifications",
  "تتحرّك": "Moving",
  "بالمحرّك": "Engine on",
  "متوقّفة": "Parked",
  "مؤجَّرة": "Rented",
  "متاحة": "Available",
  "ترجع اليوم": "Due back today",
  "بطارية منخفضة": "Low battery",
  "لا إشارة GPS": "No GPS fix",
  "بيانات قديمة": "Stale data",
  "غير متصلة": "Offline",
  "لم يتصل بعد": "Never connected",
  "خريطة": "Map",
  "قمر صناعي": "Satellite",

  "منطقة محظورة": "Restricted zone",
  "إلغاء الرسم": "Cancel drawing",
  "السرعة": "Speed",
  "الحالة": "Status",
  "كم/س": "km/h",
  "لا إشارة": "No fix",
  "🔋 البطارية": "🔋 Battery",
  "غير متوفر": "n/a",
  "🌡️ الحرارة": "🌡️ Temperature",
  "°م": "°C",
  "فارغة تماماً": "completely empty",
  "غير متصل": "Offline",
  "الآن": "now",
  "آخر موقع معروف": "Last known location",
  "جاري تحديد العنوان...": "Resolving address…",
  "جاري تحديد المنطقة…": "Finding area…",
  "خريطة PACI": "PACI map",
  "اعرض العقود النشطة": "Show active contracts",
  "اعرض كل العقود": "Show all contracts",
  "العقود المدفوعة — الأكبر أولاً": "Paid contracts — largest first",
  "العقود التي فيها رصيد — الأكبر أولاً": "Contracts with a balance — largest first",
  "العقود المستحقة على العملاء — الأكبر أولاً": "Contracts with money owed — largest first",
  "كل الفترة": "All time",
  "العقود النشطة تُعرض كلّها أيّاً كان تاريخ بدايتها": "Active contracts are always shown, whatever their start date",
  "حسب تاريخ بداية العقد": "By contract start date",
  "الرقم الآلي للعنوان": "PACI address number",
  "موقع البيت": "Home location",
  "خرائط Google": "Google Maps",
  "كويت فايندر": "Kuwait Finder",
  "الرقم الآلي للعنوان (من البطاقة المدنية)": "PACI address number (from the civil ID)",
  "افتح كويت فايندر": "Open Kuwait Finder",
  "موقع البيت — الصق الإحداثيات أو رابط كويت فايندر": "Home location — paste the coordinates or a Kuwait Finder link",
  "مسح موقع البيت؟": "Clear the home location?",
  "لا يوجد موقع": "No location",
  "د": "min",
  "متبقي عليه": "outstanding",
  "نشطة": "Active",
  "هذا الشهر": "this month",
  "مؤجَّرة حالياً": "Currently rented",
  "البطارية": "Battery",

  "موقوفة": "Suspended",
  "د.ك": "KWD",

  "س": "h",
  "ساعة": "hour",
  "آخر موقع قبل": "Last fix",

  // ── مشاركة رابط التتبّع ──────────────────────────────────────────────────
  "تعذر إنشاء رابط التتبع": "Could not create the tracking link",
  "تعذر تحديث الرابط": "Could not update the link",
  "تعذر إنشاء الرابط": "Could not create the link",
  "الرابط المباشر جاهز": "Live link ready",
  "إنشاء رابط تتبّع مباشر": "Create a live tracking link",
  "جهة حكومية": "Government body",
  "عميل أو طرف خارجي": "Customer or third party",
  "موظف مؤقت": "Temporary staff",
  "عميل / خارجي": "Customer / external",
  "للمخافر والتحقيقات": "For police and investigations",
  "متابعة السيارة المستأجرة": "Follow the rented car",
  "دخول داخلي محدود": "Limited internal access",
  "يوم": "day",
  "منها أيام إضافية بعد انتهاء المدة": "of which extra days after the term ended",
  "أسبوع": "week",
  "شهر": "month",
  "١٢ ساعة": "12 hours",
  "٣ أيام": "3 days",
  "تنتهي بعد ساعة": "Expires in an hour",
  "تنتهي بعد ١٢ ساعة": "Expires in 12 hours",
  "تنتهي غداً": "Expires tomorrow",
  "تنتهي بعد ٣ أيام": "Expires in 3 days",
  "تنتهي بعد أسبوع": "Expires in a week",
  "تنتهي بعد شهر": "Expires in a month",
  "— بالتحديد": "— precisely",
  "الموقع الحي على الخريطة": "Live location on the map",
  "السرعة والاتجاه": "Speed and heading",
  "بيانات السائق والهاتف": "Driver name and phone",
  "مفعّل دائماً ولا يمكن تعطيله": "Always on; cannot be disabled",
  "يعرض سرعة السيارة واتجاه حركتها": "Shows the car's speed and heading",
  "يعرض اسم السائق ورقم هاتفه للمستلم": "Shows the driver's name and phone to the recipient",
  "سيظهر للمستلم: الموقع الحي": "The recipient will see: live location",
  " + السرعة والاتجاه": " + speed and heading",
  " + بيانات السائق": " + driver details",
  "نُسخ": "Copied",
  "نسخ": "Copy",
  "مُعطّل": "Disabled",
  "نشط": "Active",
  "إعادة تفعيل": "Re-enable",
  "تعطيل فوراً": "Disable now",
  "إيقاف الرابط الآن": "Stop this link now",
  "موقوف": "Stopped",
  "منتهي": "Expired",
  "مشاهدة": "views",
  "لم يُفتح بعد": "not opened yet",
  "آخرها": "last",
  "أنشأه": "Created by",
  "أُنشئ": "Created",
  "ينتهي الرابط في": "The link expires on",
  "جاري الإنشاء...": "Creating…",
  "إنشاء رابط الرحلة": "Create trip link",

  // ── التشغيل والمخطّط الزمنيّ ─────────────────────────────────────────────
  "اليوم كامل": "Full day",
  "الصباح الباكر": "Early morning",
  "بعد الثامنة": "After eight",
  "آخر ساعة": "Last hour",
  "كم": "km",
  "سرعة العرض": "Playback speed",

  // ── سجلّ الحسّاس ─────────────────────────────────────────────────────────
  "٢٤ ساعة": "24 hours",
  "٧ أيام": "7 days",
  "٣٠ يوماً": "30 days",
  "الأدنى": "Min",
  "المتوسط": "Avg",
  "الأعلى": "Max",
  "نقطة": "points",
  "الخط الأحمر = الحدّ الأعلى": "red line = upper limit",
  "اترك الحدّ فارغاً ليُلغى تنبيهه. «بعد» = دقائق التجاوز المتصل قبل إرسال التنبيه — فتح باب لدقيقة لا يُزعجك.":
    "Leave a limit empty to disable its alert. “After” = minutes of continuous breach before the alert is sent — a door open for a minute won't bother you.",
  "جاري الحفظ...": "Saving…",
  "حفظ التعديلات": "Save changes",

  // ── بطاقة السيارة والشريط الجانبيّ ───────────────────────────────────────
  "الموقع": "Location",
  "الاتجاه": "Heading",
  "الأقمار الصناعية": "Satellites",
  "رحلات": "Trips",
  "كم اليوم": "km today",
  "كم بالفترة": "km in period",
  "تنبيهات": "Alerts",
  "توقفات": "Stops",
  "الفترة": "Period",
  "التاريخ": "Date",
  "بطارية الجهاز": "Device battery",
  "العداد": "Odometer",
  "الحرارة": "Temperature",
  "الرطوبة": "Humidity",
  "بطارية الحسّاس": "Sensor battery",
  "فولت": "V",
  "غير محدد": "Not set",
  "متأخرة": "Overdue",
  "باقي": "Left",
  "متبقي": "Outstanding",
  "شاغرة — بدون عقد": "Available — no contract",
  "مؤجرة": "Rented",
  "الموقع المعروض هو": "The shown position is",
  "— الجهاز لا يلتقط إشارة GPS": "— the device has no GPS fix",
  "الجهاز متصل لكنه لا يلتقط إشارة أقمار GPS — الموقع المعروض هو آخر موقع معروف":
    "The device is online but has no GPS satellite fix — the position shown is the last known one",
  "التُقط قبل": "captured",
  "وقد تكون السيارة تتحرك فعلياً. تأكد أن السيارة تحت سماء مكشوفة وافحص هوائي GPS إذا استمرت الحالة.":
    "The car may actually be moving. Make sure it is under open sky, and check the GPS antenna if this persists.",

  // ── الخريطة والمناطق المحظورة ────────────────────────────────────────────
  "تعذر تحميل الأجهزة": "Could not load devices",
  "مركبة": "vehicles",
  "حذف المنطقة المحظورة": "Delete the restricted zone",
  "أدخل اسم المنطقة أولاً": "Enter the zone name first",
  "فشل الحفظ": "Save failed",
  "حدد 3 نقاط على الأقل على الخريطة": "Mark at least 3 points on the map",
  "فشل حفظ بيانات الجهاز": "Failed to save the device",
  "اضغط على الخريطة لتحديد الحدود": "Click the map to mark the boundary",
  "حدود دقيقة": "Exact boundary",
  "حدود تقريبية": "Approximate boundary",

  // ── تنبيهات السرعة (نافذة داخل صفحة التتبّع) ─────────────────────────────
  "سجل التجاوزات": "Violation log",
  "مجموعات الحدود": "Limit groups",
  "الحد": "Limit",
  "أُبلغ": "notified",
  "تفعيل تنبيهات السرعة": "Enable speed alerts",
  "إرسال رسالة واتساب للعميل المستأجر عند التجاوز": "Send the renting customer a WhatsApp message on a violation",
  "ملخص يومي صباحي (07:30) بتجاوزات أمس": "Daily 07:30 summary of yesterday's violations",
  "الحد العام (كم/س)": "Global limit (km/h)",
  "التهدئة (دقائق)": "Cooldown (minutes)",
  "جاري الحفظ…": "Saving…",
  "حفظ الإعدادات": "Save settings",
  "فشل حفظ الإعدادات": "Failed to save the settings",
  "حد السرعة (كم/س)": "Speed limit (km/h)",
  "واتساب المتابع (اختياري) — يستلم تنبيهات سيارات المجموعة حصرياً بدل المستلمين العامّين":
    "Follower's WhatsApp (optional) — receives this group's alerts exclusively, instead of the general recipients",
  "سيارات المجموعة": "Group vehicles",
  "✓ تحديد المعروض": "✓ Select shown",
  "إنشاء المجموعة": "Create group",
  "الاسم وحد السرعة مطلوبان": "Name and speed limit are required",
  "فشل حفظ المجموعة": "Failed to save the group",
  "حذف مجموعة": "Delete group",
  "سياراتها ترجع للحد العام.": "Its vehicles revert to the global limit.",
  "مجموعة جديدة (سيارات + حد + متابع)": "New group (vehicles + limit + follower)",
  "لا توجد مجموعات — كل السيارات على الحد العام (أو حدها الخاص)":
    "No groups — every vehicle uses the global limit (or its own)",
  "سيارة": "vehicles",
  "سيارة أخرى": "more vehicles",
  "إخفاء القائمة": "Hide list",
  "، ": ", ",
  "؟": "?",

  // ── الصيانة (وجداول القوائم المشتركة) ────────────────────────────────────
  "تعذّر حفظ نطاق القاعدة": "Could not save the rule's scope",
  "القاعدة تنطبق على": "The rule applies to",
  "القاعدة تنطبق على كل السيارات": "The rule applies to all vehicles",
  "تعذّر إضافة القاعدة": "Could not add the rule",
  "تمت إضافة القاعدة": "Rule added",
  "حذف هذه القاعدة؟": "Delete this rule?",
  "تعذّر تغيير المجموعة": "Could not change the group",
  "انتقلت السيارة لمجموعة": "Vehicle moved to group",
  "أُزيلت السيارة من المجموعات": "Vehicle removed from all groups",
  "فترة السيرفس صارت": "Service interval is now",
  "فشل الإضافة": "Add failed",
  "تمت إضافة": "Added",
  "سيارة من التتبع المباشر": "vehicles from live tracking",
  "جميع أجهزة التتبع مسجلة مسبقاً": "All trackers are already registered",
  "فشلت مزامنة أجهزة التتبع": "Tracker sync failed",
  "فشل الإرسال": "Sending failed",
  "لا توجد سيارات مستحقة للصيانة حالياً": "No vehicles are due for service right now",
  "تم إرسال تنبيهات": "Alerts sent for",
  "سيارة من أصل": "of",
  "مستحقة": "due",
  "فشل إرسال التنبيهات": "Failed to send the alerts",
  "لا توجد سيارات مستحقة لتنبيه التأمين": "No vehicles are due for an insurance alert",
  "تم إرسال تنبيهات التأمين لـ": "Insurance alerts sent for",
  "فشل": "Failed",
  "❌ فشل الاتصال بالخادم": "❌ Could not reach the server",
  "هل تريد فك ربط الجهاز": "Unlink device",
  "من هذه السيارة": "from this vehicle",
  "تم فك ربط الجهاز ✅": "Device unlinked ✅",
  "تم الإرسال لـ": "Sent to",
  "مستلم": "recipients",
  "تم تنزيل جدول الصيانة Excel": "Maintenance schedule downloaded (Excel)",
  "فشل تنزيل ملف Excel": "Excel download failed",
  "تم تحديث": "Updated",
  "، وتعذر تحديث": ", and could not update",
  "صف": "rows",
  "فشل رفع أو قراءة ملف Excel": "Could not upload or read the Excel file",
  "اسم السيارة مطلوب": "Vehicle name is required",
  "تأكد من قيم العدادات وفترة السيرفس": "Check the odometer values and the service interval",
  "آخر صيانة لا يمكن أن تكون أعلى من العداد الفعلي": "Last service cannot exceed the actual odometer",
  "تم حفظ بيانات السيارة": "Vehicle saved",
  "✅ تم حفظ مستلمي التنبيهات": "✅ Alert recipients saved",
  "إلغاء العقد": "Cancel contract",
  "فشل إلغاء العقد": "Could not cancel the contract",
  "تم إلغاء العقد": "Contract cancelled",
  "تم تسجيل الصيانة — العداد": "Service recorded — odometer",
  "فشل تسجيل الصيانة": "Could not record the service",
  "فشل رفع المرفق": "Attachment upload failed",
  "نوع الصيانة مطلوب": "Service type is required",
  "اختر السيارة": "Choose the vehicle",
  "تم تحديث أمر الصيانة": "Updated work order",
  "تم إنشاء أمر الصيانة ✅": "Work order created ✅",
  "حذف أمر الصيانة": "Delete work order",
  "فشل الحذف": "Delete failed",
  "تم حذف أمر الصيانة": "Deleted work order",
  "سيارة في الأسطول": "vehicles in the fleet",
  "بحث بالسيارة، اللوحة، العميل...": "Search by vehicle, plate, customer…",
  "جاري التنزيل...": "Downloading…",
  "تنزيل Excel": "Download Excel",
  "جاري الرفع...": "Uploading…",
  "رفع Excel": "Upload Excel",
  "جارٍ المزامنة…": "Syncing…",
  "مزامنة التتبع": "Sync trackers",
  "جاري الإرسال...": "Sending…",
  "تنبيهات الصيانة": "Service alerts",
  "تنبيهات التأمين": "Insurance alerts",
  "قواعد التنبيهات": "Alert rules",
  "(مثال: تغيير زيت كل 5000 كم، فحص إطارات كل 30 يوم)": "(e.g. oil change every 5,000 km, tyre check every 30 days)",
  "تُقاس القواعد من آخر صيانة مسجّلة للسيارة، إضافةً إلى الفاصل الخاص بكل سيارة.": "Rules are measured from each vehicle's last recorded service, on top of its own interval.",
  "لا توجد قواعد بعد — التنبيه الحالي يعتمد على فاصل كل سيارة فقط.": "No rules yet — alerts currently rely on each vehicle's own interval.",
  "كل": "every",
  "حدد السيارات التي تنطبق عليها القاعدة": "Choose the vehicles this rule applies to",
  "كل السيارات": "All vehicles",
  "مفعّلة": "Enabled",
  "حذف القاعدة": "Delete rule",
  "ابحث بالاسم أو اللوحة...": "Search by name or plate…",
  "محددة": "selected",
  "لم تحدد شيئاً = كل السيارات": "Nothing selected = all vehicles",
  "السيارات المرتبطة بقاعدة أخرى لا تظهر هنا": "Vehicles bound to another rule are not listed here",
  "حفظ النطاق": "Save scope",
  "اسم القاعدة (تغيير زيت...)": "Rule name (oil change…)",
  "كل كم؟ (كم)": "Every how much? (km)",
  "كل كم يوم؟": "Every how many days?",
  "إضافة قاعدة": "Add rule",
  "مستلمو تنبيهات الصيانة والتأمين": "Maintenance & insurance alert recipients",
  "تُطبَّق على جميع السيارات — تُرسل التنبيهات يومياً 8:00 صباحاً بتوقيت الكويت.": "Applies to every vehicle — alerts are sent daily at 08:00 Kuwait time.",
  "إرسال التنبيه إلى:": "Send the alert to:",
  "الموظفين فقط": "Staff only",
  "الموظفين + العملاء": "Staff + customers",
  "لا يوجد وكلاء مسجلون بأرقام هواتف": "No agents have a phone number on file",
  "الزبون المستأجر يوصله التنبيه تلقائياً مع الموظفين.": "The renting customer receives the alert automatically along with staff.",
  "يُرسل للموظفين فقط — لا يصل الزبائن.": "Sent to staff only — customers do not receive it.",
  "مستلمو تنبيهات الواتساب": "WhatsApp alert recipients",
  "صيانة · مناطق محظورة · فصل كهرباء · انقطاع اتصال · سرعة · حرارة — تصل التنبيهات من رقم واتساب المنشأة مباشرة.": "Service · restricted zones · power cut · connection loss · speed · temperature — alerts arrive from the company's own WhatsApp number.",
  "(إرسال الواتساب موقوف لشركتكم حالياً من إدارة المنصّة)": "(WhatsApp sending is currently disabled for your company by the platform administration)",
  "الاسم": "Name",
  "+ إضافة مستلم": "+ Add recipient",
  "بلغت الحد الأقصى — تواصل مع مزوّد الخدمة لرفعه": "Limit reached — contact your provider to raise it",
  "أرقام": "numbers",
  "بدون اسم": "Unnamed",
  "🚨 تنبيه الحوادث": "🚨 Crash alerts",
  "عند اكتشاف الجهاز حادثاً — واتساب + إشعار لمن فعّل «حادث» بالقائمة أعلاه": "When the device detects a crash — WhatsApp + notification to everyone with “Crash” enabled above",
  "مفعّل": "On",
  "متوقّف": "Off",
  "بلا موقع": "No location",
  "لا توجد حوادث مسجّلة ✅": "No crashes recorded ✅",
  "⏱️ محرّك يعمل بلا حركة (خمول)": "⏱️ Engine running while stationary (idling)",
  "السيارة واقفة والمحرّك شغّال أطول من الحد — إهدار وقود": "The vehicle is parked with the engine running past the limit — wasted fuel",
  "ينبّه بعد": "Alert after",
  "دقيقة خمول متواصل": "minutes of continuous idling",
  "ابحث برقم اللوحة أو اسم العميل…": "Search by plate or customer name…",
  "دق": "min",
  "لا نتائج مطابقة لبحثك": "No results match your search",
  "لا توجد جلسات خمول مسجّلة": "No idling sessions recorded",
  "م": "#",
  "السيارة": "Vehicle",
  "جهاز GPS": "GPS device",
  "المستأجر": "Renter",
  "تلفون": "Phone",
  "العقد": "Contract",
  "عداد GPS": "GPS odometer",
  "آخر صيانة": "Last service",
  "المتبقي": "Remaining",
  "إجراءات": "Actions",
  "فك ربط الجهاز": "Unlink device",
  "فك": "Unlink",
  "غير مربوط": "Not linked",
  "حجز": "Booking",
  "إلغاء الحجز": "Cancel booking",
  "لا يوجد": "None",
  "متأخرة!": "Overdue!",
  "صيانة": "Service",
  "السجل": "Log",
  "تشخيص جهاز GPS": "GPS device diagnosis",
  "تشخيص AI": "AI diagnosis",
  "اسم السيارة": "Vehicle name",
  "العداد الفعلي": "Actual odometer",
  "فترة السيرفس": "Service interval",
  "تحددها المجموعة تلقائياً": "Set automatically by the group",
  "مجموعة الصيانة": "Service group",
  "— بدون مجموعة —": "— no group —",
  "قاعدة عامة تشمل كل السيارات تلقائياً": "A global rule that covers every vehicle automatically",
  " (عامة — تشمل الكل)": " (global — covers all)",
  "آخر صيانة أعلى من العداد الفعلي. صحح القيمة قبل الحفظ.": "Last service is higher than the actual odometer. Correct it before saving.",
  "سجل الصيانة": "Service history",
  "جاري التحميل...": "Loading…",
  "الاستعلام عن مستأجر": "Renter check",
  "حالته عبر كلّ الفروع قبل تسليم المفتاح": "Their status across all branches, before you hand over the key",
  "الرقم المدنيّ أو الهاتف أو الاسم": "Civil ID, phone, or name",
  "استعلام": "Check",
  "أدخل ٣ أحرف على الأقل": "Enter at least 3 characters",
  "تعذّر إتمام الاستعلام": "The check could not be completed",
  "لا يوجد مستأجر بهذا الرقم في سجلّاتنا": "No renter with this number in our records",
  "وهذا لا يعني أنّه سليم — يعني أنّه لم يتعامل معنا من قبل": "That does not mean they are clear — it means they have never dealt with us",
  "سليم": "Clear",
  "تنبيه": "Caution",
  "ممنوع من التأجير": "Do not rent",
  "الفروع": "Branches",
  "تسجيل منع": "Add a block",
  "رفع المنع": "Lift the block",
  "رفع المنع عن هذا المستأجر؟": "Lift the block on this renter?",
  "رُفع المنع": "Block lifted",
  "تعذّر رفع المنع": "Could not lift the block",
  "سُجّل المنع": "Block saved",
  "تعذّر حفظ المنع": "Could not save the block",
  "اكتب السبب": "Write the reason",
  "قرارٌ يراه كلّ من يستعلم عن هذا الشخص — اكتب سبباً يفهمه من يقرؤه بعدك": "Everyone who checks this person sees this — write a reason the next reader will understand",
  "السبب": "Reason",
  "مثال: أتلف السيارة ورفض الدفع — عقد 30009120": "e.g. Damaged the car and refused to pay — contract 30009120",
  "مدّة المنع (شهور)": "Block duration (months)",
  "صفر = بلا نهاية. والمدّة تحمي من وصمةٍ أبديّة على إنسان.": "Zero = indefinite. A duration keeps this from becoming a permanent mark on a person.",
  "تسجيل المنع": "Save block",
  "مثال: 290010100123": "e.g. 290010100123",
  "عقود سابقة": "Past contracts",
  "إجمالي المفوتر": "Total billed",
  "إجمالي المقبوض": "Total received",
  "المتبقّي": "Outstanding",
  "أوّل تعامل": "First dealt",
  "آخر تعامل": "Last dealt",
  "آخر تحديث للبيانات": "Data last synced",
  "مطابقات": "Matches",
  "ملاحظات المكتب": "Office notes",
  "لا توجد سجلات صيانة بعد": "No service records yet",
  "بواسطة": "By",
  "ملاحظات": "Notes",
  "جيدة": "Good",
  "قريبة": "Due soon",
  "+ أمر صيانة جديد": "+ New work order",
  "قيد التنفيذ": "In progress",
  "لا أوامر مكتملة هذا الشهر": "No orders completed this month",
  "مكتمل هذا الشهر": "completed this month",
  "تعديل أمر الصيانة": "Edit work order",
  "أمر صيانة جديد": "New work order",
  "ابحث بالاسم أو اللوحة لتصفية القائمة...": "Search by name or plate to filter the list…",
  "— اختر السيارة —": "— choose a vehicle —",
  "أمر مكتمل — لا يمكن تغيير سيارته لأن صيانتها سُجّلت": "Completed order — its vehicle cannot be changed because the service was already recorded",
  "نوع الصيانة *": "Service type *",
  "نوع الصيانة": "Service type",
  "مثال: تغيير زيت وفلتر": "e.g. oil and filter change",
  "التكلفة": "Cost",
  "المورد": "Vendor",
  "اسم الكراج / المورد": "Garage / vendor name",
  "تاريخ الدخول": "Date in",
  "تاريخ الخروج": "Date out",
  "مرفقات": "Attachments",
  "+ إضافة مرفق": "+ Add attachment",
  "إضافة مرفق": "Add attachment",
  "لا مرفقات بعد": "No attachments yet",
  "إزالة المرفق": "Remove attachment",
  "اختر السيارة ثم عبّئ بيانات الأمر": "Choose a vehicle, then fill in the order",
  "حذف الأمر": "Delete order",
  "تنزيل": "Download",
  "إنشاء الأمر": "Create order",
  "جاري الفحص...": "Checking…",
  "موعد الصيانة القادم": "Next service due",
  "التنبيهات": "Alerts",
  "معطّلة": "Disabled",
  "المستلمون:": "Recipients:",
  "النتيجة:": "Result:",
  "كل شيء مرتّب — الجهاز سيرسل التنبيه عند الاستحقاق": "All good — the device will send the alert when it comes due",
  "إرسال تنبيه اختباري الآن": "Send a test alert now",
  "تسجيل صيانة": "Record service",
  "سيُسجَّل العداد الحالي كآخر صيانة": "The current odometer will be recorded as the last service",
  "ملاحظات (اختياري)": "Notes (optional)",
  "مثال: تغيير زيت وفلتر، فحص إطارات...": "e.g. oil and filter change, tyre check…",
  "تأكيد الصيانة": "Confirm service",
  "رقم الأمر": "Order no.",
  "النوع": "Type",
  "دخول": "In",
  "خروج": "Out",
  "المرفقات": "Attachments",
  "جديد": "New",
  "قيد المراجعة": "Under review",
  "بانتظار قطع غيار": "Awaiting parts",
  "مكتمل": "Completed",
  "ملغي": "Cancelled",
  "السيارات المستحقة": "Vehicles due",
  "أوامر الصيانة": "Work orders",
  "التنبيهات والمستلمون": "Alerts & recipients",
  "مناطق": "Zones",
  "كهرباء": "Power",
  "سرعة": "Speed",
  "حادث": "Crash",
  "خمول": "Idling",
  "حرارة": "Temp",
  "لا توجد أوامر صيانة": "No work orders",
  "السابق": "Previous",
  "التالي": "Next",

  // ── لوحة الأداء والعقود ───────────────────────────────────────────────────
  // «شهري/يومي/نقدي/فور رنت/سليمة» تُترجَم للعرض فقط — القيمة المحفوظة في
  // قاعدة البيانات تبقى عربية كما هي، وإلّا انكسرت المقارنات والصفوف القديمة.
  "اختر الفترة": "Select period",
  "هذا الأسبوع": "This week",
  "الشهر الماضي": "Last month",
  "فترة مخصصة": "Custom range",
  "من تاريخ": "From date",
  "إلى تاريخ": "To date",
  "محادثاتي المفتوحة الآن": "My open conversations",
  "محادثات الفترة": "Conversations in period",
  "متوسط ردّي الأول": "My average first reply",
  "الأعمال": "Business",
  "نظرة عامة": "Overview",
  "العمليات": "Operations",
  "تعذّر تحميل بيانات الأعمال": "Could not load business data",
  "الإيرادات": "Revenue",
  "المصروفات": "Expenses",
  "الصافي": "Net",
  "من بيانات نظام التأجير (سندات القبض والمصروفات) للفترة المحددة": "From the rental system (receipts and expenses) for the selected period",
  "عقود نشطة": "Active contracts",
  "مستحقات غير محصلة": "Uncollected dues",
  "تراكمي على كل العقود المفتوحة الآن — لا يتأثر بفلتر الفترة · التفاصيل في لوحة التحصيل": "Cumulative across all currently open contracts — unaffected by the period filter · details in the collections board",
  "السيارات المؤجرة": "Rented vehicles",
  "أوامر صيانة مفتوحة": "Open work orders",
  "اضغط لعرض صفحة الصيانة": "Click to open the maintenance page",
  "إجمالي المحادثات": "Total conversations",
  "مرتفع": "High",
  "منخفض": "Low",
  "عن الفترة السابقة": "vs. previous period",
  "متوسط وقت الرد": "Average response time",
  "لا توجد بيانات كافية": "Not enough data",
  "تحسن": "improved",
  "تراجع": "worsened",
  "عبر": "across",
  "محادثة": "conversations",
  "نسبة الحل": "Resolution rate",
  "محادثات متأخرة": "Overdue conversations",
  "عميل ينتظر الرد > ٣٠ دقيقة": "Customers waiting more than 30 minutes",
  "حجم المحادثات": "Conversation volume",
  "توزيع العلامات": "Tag distribution",
  "المحادثات لكل وكيل": "Conversations per agent",
  "وقت الرد لكل وكيل (دقيقة)": "Response time per agent (minutes)",
  "محادثات فائتة": "Missed conversations",
  "بدون رد منذ أكثر من 24 ساعة": "No reply for over 24 hours",
  "متوسط وقت الحل": "Average resolution time",
  "من فتح المحادثة حتى إغلاقها": "From opening the conversation to closing it",
  "مخالفات SLA": "SLA breaches",
  "خلال الفترة المحددة": "During the selected period",
  "إجمالي الوكلاء": "Total agents",
  "متاح الآن": "available now",
  "حمل الوكلاء": "Agent load",
  "متاح": "Available",
  "غير متاح": "Unavailable",
  "لا يوجد وكلاء": "No agents",
  "أوقات الذروة (توزيع بالساعة)": "Peak hours (hourly distribution)",
  "إجمالي الإيرادات": "Total revenue",
  "للفترة المحددة": "For the selected period",
  "عدد الحجوزات": "Bookings",
  "حجوزات اليوم": "Today's bookings",
  "تبدأ اليوم": "starting today",
  "الإيرادات اليومية (د.ك)": "Daily revenue (KWD)",
  "لا توجد حجوزات اليوم": "No bookings today",
  "معلق": "Pending",
  "مؤكد": "Confirmed",
  "عدد الأشهر": "Number of months",
  "عدد الأسابيع": "Number of weeks",
  "عدد الأيام": "Number of days",
  "إلغاء هذا العقد؟": "Cancel this contract?",
  "عقداً": "contracts",
  "المدفوع": "Paid",
  "المستحق": "Due",
  "عقود التأجير": "Rental contracts",
  "المتابعة المالية للعقود — التحصيل، المدفوعات، والمستحقات": "Financial follow-up — collections, payments and dues",
  "عقد جديد": "New contract",
  "ابحث بالاسم، اللوحة، أو رقم العقد…": "Search by name, plate or contract number…",
  "تم إنشاء العقد": "Contract created",
  "فشل إنشاء العقد": "Could not create the contract",
  "تم الحفظ": "Saved",
  "تعذر حفظ رقم الهاتف": "Could not save the phone number",
  "عقد تأجير جديد": "New rental contract",
  "عقد رقم": "Contract no.",
  "عقد التأجير": "Rental contract",
  "إستلام السيارة": "Vehicle return",
  "الدفعات وكشف الحساب": "Payments & statement",
  "ملاحظات ومتابعة": "Notes & follow-up",
  "أرشيف المستندات": "Document archive",
  "بيانات العقد": "Contract details",
  "فور رنت": "Four Rent",
  "كابري": "Capri",
  "مثال:": "e.g.",
  "3 أشهر": "3 months",
  "30 يوم": "30 days",
  "القيمة": "Value",
  "ابحث بالاسم أو التلفون، أو أدخل اسماً جديداً": "Search by name or phone, or type a new name",
  "سائق ثاني": "Second driver",
  "حفظ العقد": "Save contract",
  "تم تسجيل الاستلام": "Return recorded",
  "فشل تسجيل الدفعة": "Could not record the payment",
  "تمت إضافة الدفعة": "Payment added",
  "بيانات الاستلام": "Return details",
  "المبالغ": "Amounts",
  "حالة السيارة": "Vehicle condition",
  "تسجيل الاستلام": "Record return",
  "لا توجد دفعات": "No payments",
  "إضافة دفعة (سند قبض)": "Add payment (receipt)",
  "إضافة الدفعة": "Add payment",
  "تم رفع المستند": "Document uploaded",
  "فشل الرفع": "Upload failed",
  "جارٍ الرفع...": "Uploading…",
  "رفع مستند": "Upload document",
  "لا توجد مستندات": "No documents",
  "رقم العقد": "Contract no.",
  "الإجمالي": "Total",
  "مسترجع": "Returned",
  "لا توجد عقود مطابقة": "No matching contracts",
  "جرّب تعديل البحث أو الفلتر": "Try adjusting the search or filter",
  "المكتب": "Office",
  "نوع العقد": "Contract type",
  "تاريخ الانتهاء المتوقع": "Expected end date",
  "كم/العداد عند التسليم": "Odometer at handover (km)",
  "كم/اليوم": "km per day",
  "فلس/كم": "Fils per km",
  "منطقة تسليم السيارة": "Handover area",
  "إيجار الشهر/اليوم": "Monthly / daily rent",
  "أجرة سائق": "Driver fee",
  "مبلغ إضافي": "Extra amount",
  "خصم": "Discount",
  "المدفوع مقدماً": "Paid in advance",
  "الرقم المدني": "Civil ID",
  "الجنسية": "Nationality",
  "تلفون 1": "Phone 1",
  "رقم الإجازة": "Licence number",
  "إنتهاء الإجازة": "Licence expiry",
  "عنوان السكن": "Home address",
  "التلفون": "Phone",
  "تاريخ الاستلام": "Return date",
  "كم/تأجير (عند الاستلام)": "Odometer at return (km)",
  "كم/استهلاك": "km used",
  "كم/مجاني": "Free km",
  "كم/زيادة": "Extra km",
  "مبلغ مقترح": "Suggested amount",
  "مبلغ كم زيادة": "Extra km charge",
  "مبلغ تلفيات": "Damage amount",
  "مبلغ تأخير": "Late fee",
  "حالة السيارة عند الاستلام": "Vehicle condition at return",
  "وصف الأضرار": "Damage description",
  "ملاحظات الاستلام": "Return notes",
  "المبلغ": "Amount",
  "الطريقة": "Method",
  "البنك": "Bank",
  "رقم الشيك": "Cheque number",
  "نوع المستند": "Document type",
  "شهري": "Monthly",
  "يومي": "Daily",
  "أسبوعي": "Weekly",
  "سليمة": "Undamaged",
  "بها أضرار": "Damaged",
  "تالفة": "Written off",
  "تاريخ البداية": "Start date",
  "تاريخ البداية (مقفل)": "Start date (locked)",
  "عقود": "contracts",
  "عقود مسترجعة": "Returned contracts",
  "عقود ملغاة": "Cancelled contracts",

  "المستند": "Document",

  // ── الإعدادات ── يفتحها كل مستخدم، وفيها مبدّل اللغة نفسه.
  // ملاحظة: نصوص `buildInstructions` ليست هنا عمداً — تُرسَل تعليماتٍ للبوت
  // لا تُعرَض على شاشة، وترجمتها تجعله يردّ إنجليزيّاً على عملاءَ عرب.
  "تعذر الاتصال": "Could not connect",
  "تعذر بدء الاستيراد": "Could not start the import",
  "ربط الجهاز (QR)": "Link device (QR)",
  "تحديث الحالة": "Refresh status",
  "استيراد جهات الاتصال والمحادثات": "Import contacts and chats",
  "جهات": "contacts",
  "محادثات": "chats",
  "رسائل": "messages",
  "واتساب ← الأجهزة المرتبطة ← ربط جهاز ← امسح الرمز": "WhatsApp → Linked devices → Link a device → scan the code",
  "✓ تم حفظ اسم الشركة": "✓ Company name saved",
  "هل أنت متأكد من حذف": "Are you sure you want to delete",
  "تعذّر إضافة القناة": "Could not add the channel",
  "حدث خطأ": "Something went wrong",
  "للترقية اضغط هنا": "Upgrade here",
  "تعذّر حذف النشاط": "Couldn't delete the business",
  "منع تشغيل هذه السيارة؟ لن تشتغل بعد إطفائها حتى تسمح بالتشغيل من جديد.": "Block this car from starting? Once switched off it won't start until you allow it again.",
  "السماح بتشغيل هذه السيارة من جديد؟": "Allow this car to start again?",
  "تعذّر الاتصال بالخادم": "Couldn't reach the server",
  "أكّد الجهاز منع التشغيل": "The device confirmed the start block",
  "أكّد الجهاز السماح بالتشغيل": "The device confirmed starting is allowed",
  "أُرسل الأمر ولم يردّ الجهاز — تحقّق قبل الإعادة": "Command sent but the device didn't reply — check before retrying",
  "ردّ الجهاز بما لم يُتوقّع": "Unexpected device reply",
  "تعذّر إرسال الأمر": "Couldn't send the command",
  "التشغيل ممنوع": "Starting blocked",
  "التشغيل مسموح": "Starting allowed",
  "حالة منع التشغيل غير معروفة": "Start-block status unknown",
  "جارٍ الإرسال…": "Sending…",
  "منع التشغيل": "Block starting",
  "السماح بالتشغيل": "Allow starting",
  "الجهاز غير متصل الآن — لا يُرسل الأمر إلا والجهاز متصل": "The device is offline — commands are only sent while it's connected",
  "آخر محاولة لم تُؤكَّد": "Last attempt not confirmed",
  "الجهاز غير متصل الآن — منع التشغيل ينتظر اتصاله (حتى ٣ دقائق) ثم يُرسل": "The device is offline — a start block waits for it to connect (up to 3 minutes), then is sent",
  "بانتظار اتصال الجهاز… يُرسل منع التشغيل لحظة اتصاله": "Waiting for the device… the start block is sent the moment it connects",
  "إلغاء الانتظار": "Cancel waiting",
  "أُلغي الانتظار — لم يُرسل منع التشغيل": "Waiting cancelled — the start block was not sent",
  "السماح بالتشغيل بالانتظار — يُرسل تلقائياً عند اتصال الجهاز": "Allow-start is queued — it is sent automatically when the device connects",
  "الجهاز غير متصل الآن — منع التشغيل لا يُرسل إلا والجهاز متصل": "The device is offline — a start block is only sent while it's connected",
  "تعذّر تحميل حالة منع التشغيل": "Couldn't load the start-block status",
  "إعادة المحاولة": "Retry",
  "منع تشغيل السيارة (قطع السلف)": "Block car starting (starter cut)",
  "هذه المحادثة غير مربوطة برقم واتساب مفعّل — اختر الرقم الذي تُرسل منه.": "This conversation isn't linked to an active WhatsApp number — choose the number to send from.",
  "لا يوجد رقم واتساب مفعّل — فعّل رقماً من الإعدادات": "No active WhatsApp number — activate one in Settings",
  "اختر رقم الإرسال": "Choose the sending number",
  "تعذّر ربط الرقم": "Couldn't link the number",
  "تم ربط المحادثة برقم الإرسال": "Conversation linked to the sending number",
  "تعذّر حفظ الحساب": "Couldn't save the account",
  "حسابات مربوطة بالطريقة القديمة (توكن يدوي) — أعد ربطها بزرّ «ربط حساب إنستغرام» ثم احذف القديم": "Accounts linked the old way (manual token) — reconnect them with “Connect Instagram account”, then delete the old one",
  "غير مفعّل": "Inactive",
  "تمت إضافة حساب واتساب": "WhatsApp account added",
  "✓ تم حفظ حساب الواتساب": "✓ WhatsApp account saved",
  "حذف هذا الحساب؟": "Delete this account?",
  "تم حذف الحساب": "Account deleted",
  "تمت إضافة حساب إنستغرام": "Instagram account added",
  "✓ تم حفظ حساب الإنستغرام": "✓ Instagram account saved",
  "تعذّرت إضافة حساب تيك توك": "Could not add the TikTok account",
  "تمت إضافة حساب تيك توك": "TikTok account added",
  "✓ تم حفظ حساب تيك توك": "✓ TikTok account saved",
  "✓ تم حفظ إعدادات البوت": "✓ Bot settings saved",
  "اسم الشركة": "Company name",
  "حفظ الاسم": "Save name",
  "البوت مفعّل ✓": "Bot enabled ✓",
  "إعدادات البوت": "Bot settings",
  "إنستغرام": "Instagram",
  "تيك توك": "TikTok",
  "🤖 بوت مفعّل": "🤖 Bot on",
  "حسابات واتساب": "WhatsApp accounts",
  "المزود:": "Provider:",
  "اسم الحساب": "Account name",
  "مثال: الرقم الرئيسي": "e.g. Main number",
  "اربط الجلسة وامسح الـ QR من": "Start the session and scan the QR from",
  "خادم WPPConnect": "the WPPConnect server",
  "نفسه (start-session)، وولّد التوكن ثم ضعه بالأعلى.": "itself (start-session), generate the token and paste it above.",
  "وجّه webhook خادم WPPConnect إلى:": "Point the WPPConnect server webhook to:",
  "احفظ الحساب أولاً، ثم اضغط": "Save the account first, then click",
  "وامسح الرمز من واتساب على جوالك.": "and scan the code from WhatsApp on your phone.",
  "في إعدادات Evolution → Webhook، ضع الرابط:": "In Evolution → Webhook settings, set the URL:",
  "وفعّل الحدث": "and enable the event",
  "تفعيل": "Enable",
  "✓ تم نقل الحساب": "✓ Account moved",
  "نقل لـ...": "Move to…",
  "إضافة حساب واتساب": "Add WhatsApp account",
  "حسابات إنستغرام": "Instagram accounts",
  "مثال: الصفحة الرئيسية": "e.g. Main page",
  "أدخل Page Access Token": "Enter the Page Access Token",
  "إضافة حساب إنستغرام": "Add Instagram account",
  "حسابات تيك توك": "TikTok accounts",
  "رابط الـ Webhook:": "Webhook URL:",
  "سجّله في TikTok Login Kit": "register it in TikTok Login Kit",
  "مثال: الحساب الرئيسي": "e.g. Main account",
  "أدخل Access Token": "Enter the Access Token",
  "ربط OAuth": "Connect with OAuth",
  "إضافة حساب تيك توك": "Add TikTok account",
  "إعدادات بوت": "Bot settings",
  "✅ البوت مفعّل": "✅ Bot enabled",
  "⭕ البوت معطّل": "⭕ Bot disabled",
  "البوت يرد تلقائياً على العملاء": "The bot replies to customers automatically",
  "الهوية": "Identity",
  "اسم البوت": "Bot name",
  "مساعد جسر": "Jisr Assistant",
  "مثال: كابري": "e.g. Capri",
  "وصف النشاط": "Business description",
  "مثال: شركة متخصصة في تأجير السيارات الفاخرة في الكويت، تقدم خدمة توصيل للمطار...": "e.g. A luxury car rental company in Kuwait offering airport delivery…",
  "✅ مسموح للبوت بـ": "✅ The bot may",
  "❌ ممنوع على البوت": "❌ The bot may not",
  "تعليمات إضافية (اختياري)": "Extra instructions (optional)",
  "أي تعليمات خاصة — مثال: إذا سأل عن التوصيل للمطار وضّح أن السعر يختلف...": "Any special instructions — e.g. if asked about airport delivery, explain the price differs…",
  "وقت الانتظار قبل تدخل الموظف": "Wait time before an agent steps in",
  "حفظ...": "Saving…",
  "حفظ إعدادات البوت": "Save bot settings",
  "تعذّر إنشاء نشاط جديد": "Could not create a new business",
  "تمت إضافة شركة جديدة": "New company added",
  "تم ربط القناة بالنشاط ✅": "Channel linked to the business ✅",
  "✓ تم حفظ إعدادات البريد": "✓ Email settings saved",
  "أدخل بريداً للاختبار": "Enter an address to test",
  "✓ تم إرسال رسالة الاختبار": "✓ Test message sent",
  "✓ تم حفظ البوابة": "✓ Gateway saved",
  "حذف هذه البوابة؟": "Delete this gateway?",
  "تم الحذف": "Deleted",
  "حذف الموقع؟": "Delete the location?",
  "إعدادات النظام": "System settings",
  "اسم الشركة العام": "Public company name",
  "يُستبدل في قوالب الرسائل بدل": "Replaces this placeholder in message templates:",
  "اسم الشركة — مثال: جسر للتقنية": "Company name — e.g. Jisr Technology",
  "الدولة (تُحدّد التوقيت في التقارير والتواريخ)": "Country (sets the timezone for reports and dates)",
  "اختر الدولة": "Choose a country",
  "✓ تم حفظ بيانات الشركة": "✓ Company details saved",
  "مواقع الفروع": "Branch locations",
  "مواقع محددة يمكن إرسالها للعملاء عبر الشات": "Pinned locations you can send to customers in chat",
  "إضافة موقع": "Add location",
  "الشركات والحسابات": "Companies and accounts",
  "كل شركة لها حسابات واتساب وإنستغرام وبوت مستقل": "Each company has its own WhatsApp, Instagram and bot",
  "إضافة شركة": "Add company",
  "🔗 روابط Webhook (مشتركة لجميع الحسابات — ضعها في Meta)": "🔗 Webhook URLs (shared by all accounts — set these in Meta)",
  "واتساب:": "WhatsApp:",
  "تم النسخ": "Copied",
  "إنستغرام:": "Instagram:",
  "لا توجد شركات بعد — اضغط \"إضافة شركة\" للبدء": "No companies yet — click “Add company” to start",
  "جارٍ…": "Working…",
  "ربط": "Link",
  "⚠️ أرقام غير مرتبطة بأي نشاط": "⚠️ Numbers not linked to any business",
  "هذي القنوات غير مرتبطة بأي نشاط (نشاطها محذوف)، والمفعّلة منها تُحسب من قنوات باقتك. اربط ما تحتاجه بنشاط ليُدار من الأعلى، وعطّل أو احذف الباقي.": "These channels belong to no business (theirs was deleted), and the active ones count toward your plan's channels. Link the ones you need to a business to manage them above, and disable or delete the rest.",
  "مفعّلة · تُحسب من الباقة": "Active · counts toward your plan",
  "غير مفعّلة": "Inactive",
  "تعطيل": "Disable",
  "حذف هذه القناة نهائياً؟ تنفصل محادثاتها عنها.": "Delete this channel permanently? Its conversations will be detached from it.",
  "حُذفت القناة": "Channel deleted",
  "عُطّلت القناة — لم تعد تُحسب من باقتك": "Channel disabled — it no longer counts toward your plan",
  "تعذّر تنفيذ العملية": "Could not complete the action",
  "إنستقرام": "Instagram",
  "🔧 إصلاح أيقونات إنستغرام": "🔧 Fix Instagram icons",
  "سيتم حذف جميع جهات اتصال إنستغرام ومحادثاتهم نهائياً. متأكد؟": "All Instagram contacts and their chats will be permanently deleted. Are you sure?",
  "🗑 حذف جميع جهات اتصال إنستغرام": "🗑 Delete all Instagram contacts",
  "بوابات الدفع": "Payment gateways",
  "إضافة بوابة": "Add gateway",
  "لا توجد بوابات — اضغط «إضافة بوابة»": "No gateways — click “Add gateway”",
  "تجريبي": "Sandbox",
  "معطل": "Disabled",
  "إعدادات البريد الإلكتروني": "Email settings",
  "بريد شركتك لإرسال كلمات المرور والتنبيهات والتقارير. اختر SMTP أو مفتاح API.": "Your company email for sending passwords, alerts and reports. Choose SMTP or an API key.",
  "طريقة الإرسال": "Sending method",
  "معطّل": "Disabled",
  "بريد الشركة": "company email",
  "مفتاح API (Resend)": "API key (Resend)",
  "اسم المُرسِل": "Sender name",
  "شركة جسر": "Jisr",
  "بريد المُرسِل (From)": "Sender address (From)",
  "خادم SMTP": "SMTP host",
  "المنفذ": "Port",
  "اسم المستخدم": "Username",
  "كلمة المرور": "Password",
  "(محفوظة)": "(saved)",
  "اتصال آمن SSL (المنفذ 465). اتركه مطفأ مع المنفذ 587 (TLS).": "Secure SSL connection (port 465). Leave off for port 587 (TLS).",
  "(محفوظ)": "(saved)",
  "جرّب الإرسال إلى": "Send a test to",
  "إرسال اختبار": "Send test",
  "Agent التقرير اليومي": "Daily report agent",
  "مفعل": "On",
  "متوقف": "Off",
  "يحلل محادثات اليوم السابق ويرسل تقريراً يومياً": "Analyses yesterday's conversations and sends a daily report",
  "تشغيل التقرير اليومي": "Enable the daily report",
  "وقت الإرسال": "Send time",
  "مرسل الإيميل": "Email sender",
  "أرقام واتساب المستلمين": "Recipient WhatsApp numbers",
  "إيميلات المستلمين": "Recipient emails",
  "تم تشغيل التقرير": "Report triggered",
  "إرسال اختباري": "Send a test",
  "تم حفظ إعدادات التقرير": "Report settings saved",
  "🌐 لغة الواجهة · Interface language": "🌐 Interface language · لغة الواجهة",
  "يُحفظ لحسابك ويتبعك على أي جهاز · Saved to your account, on every device": "Saved to your account, on every device · يُحفظ لحسابك ويتبعك على أي جهاز",
  "التوجيه التلقائي": "Automatic routing",
  "تفعيل التوجيه التلقائي": "Enable automatic routing",
  "في حال التعطيل يتعين على المشرفين تعيين المحادثات يدوياً": "When off, supervisors must assign conversations manually",
  "استراتيجية التوزيع": "Distribution strategy",
  "الأقل عبئاً (توصية)": "Least loaded (recommended)",
  "بالتوالي": "Round robin",
  "حسب المهارة والعلامات": "By skill and tags",
  "الأعلى أداءً أولاً": "Best performer first",
  "الحد الأقصى للمحادثات المتزامنة": "Maximum concurrent conversations",
  "تم حفظ إعدادات التوجيه": "Routing settings saved",
  "تفضيلات الإشعارات": "Notification preferences",
  "نوع الحدث": "Event type",
  "إيميل": "Email",
  "تم حفظ إعدادات الإشعارات": "Notification settings saved",
  "رقم واتساب الشخصي للتنبيهات": "Personal WhatsApp number for alerts",
  "رقم الجوال (مع رمز الدولة)": "Mobile number (with country code)",
  "✓ تم حفظ رقم التنبيهات": "✓ Alert number saved",
  "محاكي رسائل الاختبار": "Test message simulator",
  "رقم الهاتف": "Phone number",
  "نص الرسالة": "Message text",
  "تغيير كلمة المرور": "Change password",
  "كلمة المرور الحالية": "Current password",
  "كلمة المرور الجديدة": "New password",
  "6 أحرف على الأقل": "At least 6 characters",
  "تأكيد كلمة المرور": "Confirm password",
  "أعد كتابة كلمة المرور": "Re-enter the password",
  "كلمتا المرور غير متطابقتين": "The passwords do not match",
  "✓ تم تغيير كلمة المرور": "✓ Password changed",
  "تعديل البوابة": "Edit gateway",
  "إضافة بوابة دفع": "Add payment gateway",
  "اسم البوابة": "Gateway name",
  "مثال: كابري - حسابي": "e.g. Capri — my account",
  "الشركة": "Company",
  "— بدون ربط —": "— not linked —",
  "دومين صفحة الدفع (اختياري)": "Payment page domain (optional)",
  "روابط الدفع لهذه الشركة تنبنى على هذا الدومين — فارغ = الدومين العام. يتطلب توجيه الدومين للسيرفر أولاً.": "This company's payment links are built on this domain — empty = the shared domain. The domain must point at the server first.",
  "وضع تجريبي (Sandbox)": "Sandbox mode",
  "تعديل الموقع": "Edit location",
  "إضافة موقع جديد": "Add a new location",
  "اسم الموقع": "Location name",
  "مثال: شركة كابري — فرع الجهراء": "e.g. Capri — Jahra branch",
  "رابط قوقل ماب": "Google Maps link",
  "مفعّل (يظهر في الشات)": "Enabled (shown in chat)",
  "الكويت": "Kuwait",
  "السعودية": "Saudi Arabia",
  "الإمارات": "UAE",
  "قطر": "Qatar",
  "البحرين": "Bahrain",
  "عُمان": "Oman",
  "الأردن": "Jordan",
  "العراق": "Iraq",
  "مصر": "Egypt",
  "لبنان": "Lebanon",
  "سوريا": "Syria",
  "اليمن": "Yemen",
  "فلسطين": "Palestine",
  "السودان": "Sudan",
  "ليبيا": "Libya",
  "تونس": "Tunisia",
  "الجزائر": "Algeria",
  "المغرب": "Morocco",
  "يرد على الاستفسارات العامة": "Answers general enquiries",
  "يعرض الأسعار": "Shows prices",
  "يأخذ حجوزات": "Takes bookings",
  "يرسل صور السيارات": "Sends vehicle photos",
  "لا يتحدث عن المنافسين": "Does not discuss competitors",
  "لا يعطي خصومات": "Does not offer discounts",
  "لا يحجز بدون تأكيد الدفع": "Does not book without confirmed payment",
  "بيانات الشركة": "Company",
  "القنوات والحسابات": "Channels & accounts",
  "البريد والتقارير": "Email & reports",
  "الإشعارات والتوزيع": "Notifications & routing",
  "الأمان": "Security",
  "عند الإشارة (@Mention)": "On @mention",
  "عند تحويل محادثة لك": "When a conversation is transferred to you",
  "تجاوز وقت الرد (SLA)": "Response time breach (SLA)",
  "يجيب على أسئلة العملاء العامة عن الخدمة": "Answers customers' general questions about the service",
  "يخبر العميل بأسعار السيارات والتفاصيل": "Tells the customer vehicle prices and details",
  "يكمل عملية الحجز كاملة مع العميل": "Completes the whole booking with the customer",
  "يرسل صور عند طلب العميل": "Sends photos when the customer asks",
  "يتجنب ذكر أي شركة منافسة أو المقارنة": "Avoids naming or comparing with any competitor",
  "لا يعد بخصم بدون موافقة الإدارة": "Never promises a discount without management approval",
  "يشترط إرسال رابط الدفع قبل تأكيد الحجز": "Requires the payment link before confirming a booking",
  "متصل ✓": "Connected ✓",
  "جارٍ الربط...": "Connecting…",
  "غير مكتمل الإعداد": "Setup incomplete",
  "رابط شعار الشركة (يظهر في تقارير PDF) — https://...": "Company logo URL (shown in PDF reports) — https://…",

  // ── صندوق الوارد ── محور العمل: المحادثات والرد والقوالب وروابط الدفع.
  // «/pay» في النصّ الإنجليزيّ مقصود: الأمر العربيّ «/دفع» لا يُكتَب من لوحة
  // مفاتيح لاتينية، فأُضيف مرادفاً في الكود ويُذكَر هنا كما يُكتَب فعلاً.
  "صورة": "photo",
  "📄 ملف — اضغط للتحميل": "📄 File — click to download",
  "هل أنت متأكد من حذف هذه المحادثة؟ سيتم حذف جميع الرسائل ولا يمكن التراجع.": "Delete this conversation? All messages will be removed and this cannot be undone.",
  "تم حذف المحادثة": "Conversation deleted",
  "تعذّر حذف المحادثة": "Could not delete the conversation",
  "فشل إنشاء رابط التقسيط": "Could not create the instalment link",
  "تم إرسال رابط تقسيط Deema للعميل عبر واتساب ✅": "Deema instalment link sent to the customer on WhatsApp ✅",
  "فشل إنشاء رابط الدفع": "Could not create the payment link",
  "تم إرسال رابط الدفع للعميل عبر واتساب ✅": "Payment link sent to the customer on WhatsApp ✅",
  "أدخل مبلغ الحجز": "Enter the booking amount",
  "فشل إنشاء رابط الحجز": "Could not create the booking link",
  "تم إرسال رابط حجز الليموزين للعميل عبر واتساب ✅": "Limo booking link sent to the customer on WhatsApp ✅",
  "تم إنشاء الرابط لكن تعذّر الإرسال عبر واتساب": "Link created, but sending on WhatsApp failed",
  "فشل رفع التسجيل": "Recording upload failed",
  "رسالة صوتية": "Voice message",
  "تعذّر الوصول إلى الميكروفون. تأكد من السماح بالإذن في المتصفح.": "Could not access the microphone. Check that permission is granted in the browser.",
  "فشل رفع الملف": "File upload failed",
  "فشل إرسال القالب": "Could not send the template",
  "تم إرسال القالب": "Template sent",
  "فشل إرسال الرسالة": "Could not send the message",
  "مدّة الهدوء قبل أن يعود البوت للردّ": "Quiet period before the bot resumes replying",
  "تغيّرت المحادثة أثناء الكتابة — أعد الإرسال بعد التأكّد من المستلم": "The conversation changed while you were typing — check the recipient and send again",
  "تعذّر إنشاء جهة الاتصال": "Could not create the contact",
  "تعذّر إنشاء المحادثة": "Could not create the conversation",
  "تم إنشاء المحادثة لكن فشل إرسال الرسالة": "Conversation created, but the message failed to send",
  "تم إرسال الرسالة ✅": "Message sent ✅",
  "حدث خطأ أثناء الإنشاء": "Something went wrong while creating",
  "محادثة جديدة": "New conversation",
  "اختيار الوكيل": "Choose agent",
  "اسم العميل (اختياري)": "Customer name (optional)",
  "اسم العميل": "Customer name",
  "رقم الواتساب": "WhatsApp number",
  "— اختر رقم الواتساب —": "— Choose a WhatsApp number —",
  "اختر رقم الواتساب الذي تُرسل منه": "Choose the WhatsApp number to send from",
  "لا يوجد حساب واتساب مفعّل": "No active WhatsApp account",
  "الرسالة الأولى": "First message",
  "اكتب رسالتك هنا...": "Type your message here…",
  "إرسال وإنشاء المحادثة": "Send and start the conversation",
  "بحث عن عميل...": "Search for a customer…",
  "المحادثات:": "Conversations:",
  "القنوات:": "Channels:",
  "رقم": "number",
  "كل القنوات": "All channels",
  "واتساب — الأرقام": "WhatsApp — numbers",
  "كل الأرقام": "All numbers",
  "كل الموظفين": "All staff",
  "لا توجد محادثات في هذا التصنيف": "No conversations in this segment",
  "لا توجد محادثات": "No conversations",
  "اختر محادثة من القائمة للبدء": "Pick a conversation from the list to start",
  "إنستغرام DM": "Instagram DM",
  "تيك توك DM": "TikTok DM",
  "متاح لـ": "Open for",
  "تريد إعادة تفعيل البوت؟ سيتولى البوت الرد على العميل.": "Re-enable the bot? It will take over replying to the customer.",
  "البوت يرد — اضغط للتدخل": "The bot is replying — click to take over",
  "أنت تتحكم — اضغط لإعادة البوت": "You are in control — click to hand back to the bot",
  "🤖 البوت يرد": "🤖 Bot replying",
  "👤 وكيل يرد": "👤 Agent replying",
  "👤 تدخل وأوقف البوت": "👤 Take over and stop the bot",
  "🤖 أعد تفعيل البوت": "🤖 Hand back to the bot",
  "إغلاق المحادثة": "Close conversation",
  "إعادة فتح المحادثة": "Reopen conversation",
  "✓ تمت المراجعة — خرجت من «متأخرة»": "✓ Reviewed — removed from “Overdue”",
  "تعذّر تنفيذ الإجراء": "Could not complete the action",
  "تمت المراجعة (لا يحتاج رد)": "Mark reviewed (no reply needed)",
  "المحادثة لم تحتوِ على معلومات مفيدة للاستخراج": "The conversation had nothing useful to extract",
  "لقاعدة المعرفة": "to the knowledge base",
  "فشل الاستخراج": "Extraction failed",
  "استخرج لقاعدة المعرفة": "Extract to knowledge base",
  "حذف المحادثة": "Delete conversation",
  "ملاحظة داخلية": "Internal note",
  "العميل": "Customer",
  "أنت": "you",
  "رد مع اقتباس": "Reply with quote",
  "↩ رد": "↩ Reply",
  "رد": "Reply",
  "اكتب ردك...": "Type your reply…",
  "إرسال الرد": "Send reply",
  "انتهت نافذة الـ 24 ساعة للواتساب. لا يمكنك إرسال رسائل حرة.": "The WhatsApp 24-hour window has closed. Free-form messages cannot be sent.",
  "إرسال قالب معتمد": "Send an approved template",
  "المحادثة مغلقة. يرجى إعادة فتحها لإرسال رسائل.": "This conversation is closed. Reopen it to send messages.",
  "اختر وكيلاً": "Choose an agent",
  "لا توجد نتائج لـ": "No results for",
  "المساعد الذكي يدير هذه المحادثة": "The AI assistant is handling this conversation",
  "تدخل الآن": "Take over now",
  "سلسلة التبريد": "Cold chain",
  "لا مركبات عليها حسّاس حرارة": "No vehicles with a temperature sensor",
  "خارج النطاق اليوم": "Out of range today",
  "لا تجاوز اليوم": "No breach today",
  "🌡️ التبريد": "🌡️ Cold chain",
  "سلسلة التبريد: حالة كل الحسّاسات": "Cold chain: all sensors",
  "أسوأ قراءة": "Worst reading",
  "الساعة": "at",
  "داخل النطاق طوال الفترة": "In range the whole period",
  "نوع البضاعة": "Cargo type",
  "مجمّد": "Frozen",
  "بارد": "Chilled",
  "عطور": "Perfumes",
  "‑18° أو أقل": "‑18° or below",
  "من 0° إلى 5°": "0° to 5°",
  "من 15° إلى 25°": "15° to 25°",
  "حدودٌ تكتبها أنت": "Your own limits",
  "داخل النطاق": "In range",
  "قريب من الحدّ": "Near limit",
  "خارج النطاق": "Out of range",
  "الحسّاس لا يرسل": "Sensor silent",
  "بلا حدود مضبوطة": "No limits set",
  "لا قراءة": "No reading",
  "آخر قراءة قبل": "Last reading",
  "ليست حيّة": "not live",
  "آخر قراءة": "Last reading",
  "آخر قراءة من الحسّاس قبل": "Last sensor reading",
  "قبل": "ago",
  "عرض الفترة كاملة": "Show whole period",
  "أعلى": "max",
  "رحلة": "Trip",
  "جارية": "ongoing",
  "الرحلات": "Trips",
  "👤 البوت مطفأ": "👤 Bot off",
  "البوت مطفأ من إعدادات المساعد — لا يرد على أحد": "The bot is switched off in the assistant settings — it replies to no one",
  "اختر موقع الفرع": "Choose a branch location",
  "موقعي الحالي (GPS)": "My current location (GPS)",
  "لا توجد مواقع محفوظة": "No saved locations",
  "استخدم موقعي الحالي": "Use my current location",
  "عقد الإيجار": "Rental contract",
  "يومي:": "Daily:",
  "إجمالي:": "Total:",
  "لا يوجد عقد نشط لهذا العميل": "This customer has no active contract",
  "إرسال رابط تقسيط Deema": "Send a Deema instalment link",
  "إرسال رابط دفع إلكتروني": "Send an online payment link",
  "اختياري": "optional",
  "البيان / الوصف": "Description",
  "دفعة عن عقد...": "Payment for contract…",
  "نوع الرابط": "Link type",
  "💳 دفعة عادية": "💳 Ordinary payment",
  "🗓️ تقسيط Deema": "🗓️ Deema instalments",
  "بوابة الدفع": "Payment gateway",
  "— تلقائي حسب الواتساب —": "— automatic, by WhatsApp number —",
  "الإرسال من واتساب": "Send from WhatsApp",
  "— تلقائي (حساب المحادثة) —": "— automatic (the conversation's account) —",
  "طريقة الدفع": "Payment method",
  "مع فيزا": "With Visa",
  "بدون فيزا": "Without Visa",
  "المبلغ (د.ك)": "Amount (KWD)",
  "إرسال": "Send",
  "إرسال رابط حجز ليموزين": "Send a limo booking link",
  "الليموزين": "Limo",
  "اختر أو اكتب رقم اللوحة": "Choose or type the plate number",
  "ملاحظة تظهر للموظف": "Note shown to staff",
  "العميل يختار الساعات ويدفع لتأكيد الحجز (يُحجز الوقت ٣٠ دقيقة بانتظار الدفع)": "The customer picks the hours and pays to confirm (the slot is held for 30 minutes awaiting payment)",
  "رد للعميل": "Reply to customer",
  "رد على": "Replying to",
  "رسالتك": "your message",
  "اكتب رسالة... (اكتب /دفع لإرسال رابط دفع)": "Type a message… (type /pay to send a payment link)",
  "اكتب ملاحظة للفريق... (استخدم @ للإشارة لموظف)": "Write a note for the team… (use @ to mention someone)",
  "جاري رفع الملف...": "Uploading…",
  "جاري التسجيل...": "Recording…",
  "إنهاء وإرفاق": "Finish and attach",
  "إنهاء": "Finish",
  "إرسال صورة": "Send a photo",
  "إرسال فيديو": "Send a video",
  "فيديو": "Video",
  "تسجيل صوتي": "Voice recording",
  "صوت": "Audio",
  "إرسال رابط دفع (/دفع)": "Send a payment link (/pay)",
  "دفع": "Pay",
  "ليموزين": "Limo",
  "قوالب جاهزة": "Saved templates",
  "قوالب": "Templates",
  "القوالب الجاهزة": "Saved templates",
  "ابحث في القوالب...": "Search templates…",
  "لا توجد قوالب معتمدة بعد": "No approved templates yet",
  "المزيد": "More",
  "تسجيل صوت": "Record audio",
  "ملف صوتي": "Audio file",
  "موقع": "Location",
  "صور سيارات": "Vehicle photos",
  "جاري...": "Working…",
  "انقر لتعديل الاسم": "Click to edit the name",
  "عميل غير معروف": "Unknown customer",
  "فتح العميل": "Open customer",
  "💳 رابط دفع": "💳 Payment link",
  "قالب": "Template",
  "إجمالي العقود": "Total contracts",
  "المستحق عليه": "Outstanding",
  "العلامات": "Tags",
  "حذف العلامة": "Remove tag",
  "علامة مخصصة...": "Custom tag…",
  "+ إضافة علامة": "+ Add tag",
  "📅 تاريخ البداية": "📅 Start date",
  "📅 تاريخ الانتهاء": "📅 End date",
  "يوم متبقي": "days left",
  "ينتهي اليوم!": "Ends today!",
  "تأخر": "Overdue by",
  "💰 سعر الإيجار الفعلي": "💰 Actual rental price",
  "إيجار شهري": "Monthly rent",
  "إيجار يومي": "Daily rent",
  "لا يوجد عقد نشط": "No active contract",
  "مسؤول المحادثة": "Conversation owner",
  "غير معين لأحد": "Unassigned",
  "(مدير)": "(Admin)",
  "(مشرف)": "(Supervisor)",
  "(وكيل)": "(Agent)",
  "ملاحظات العميل": "Customer notes",
  "تفاصيل العميل ستظهر هنا عند اختيار محادثة": "Customer details appear here when you pick a conversation",
  "السيارات الشاغرة": "Available vehicles",
  "ابحث باسم السيارة أو رقم اللوحة...": "Search by vehicle name or plate…",
  "لا توجد سيارات مطابقة": "No matching vehicles",
  "لا توجد صور لهذه السيارة": "No photos for this vehicle",
  "اختر صوراً للإرسال": "Select photos to send",
  "صورة محددة": "photos selected",
  "إلغاء الكل": "Clear all",
  "تحديد الكل": "Select all",
  "محادثاتي": "My conversations",
  "غير مسندة": "Unassigned",
  "بانتظار العميل": "Awaiting customer",
  "مغلقة": "Closed",
  "بالانتظار": "Waiting",
  "انتهت نافذة 24 ساعة": "24-hour window closed",
  "📷 صورة": "📷 Photo",
  "🎥 فيديو": "🎥 Video",
  "🎵 رسالة صوتية": "🎵 Voice message",
  "📄 ملف": "📄 File",
  "📍 موقع": "📍 Location",
  "📎 مرفق": "📎 Attachment",
  "ترحيب": "Greetings",
  "طلبات": "Orders",
  "عروض": "Offers",
  "دعم": "Support",
  "وكيل يرد": "Agent replying",

  "بلا رقم هاتف": "No phone number",
  "خط مشترك — مسجّل باسم قريب أو كفيل": "Shared line — registered to a relative or sponsor",

  // ── اختيار اللغة ─────────────────────────────────────────────────────────
  "اختر لغتك": "Choose your language",
  "تقدر تغيّرها لاحقاً من الإعدادات": "You can change this later in Settings",
  "اجعلها لغتي الافتراضية": "Make this my default language",
  "متابعة": "Continue",

  // ── فواتير أجهزة التتبّع (/device-invoices) ──────────────────────────────
  "فواتير أجهزة التتبّع": "Tracking Device Invoices",
  "الشركة:": "Company:",
  "حسابك غير مرتبط بشركة في نظام الفوترة.": "Your account isn't linked to a company in the billing system.",
  "مستحق (غير مدفوع)": "Due (unpaid)",
  "مدفوع": "Paid",
  "عدد الفواتير": "Invoices",
  "كل الفواتير": "All invoices",
  "لا توجد فواتير بعد. تظهر هنا أول كل شهر.": "No invoices yet. They appear here at the start of each month.",
  "جهاز": "devices",
  "يوم-جهاز": "device-days",
  "مدفوعة": "Paid",
  "غير مدفوعة": "Unpaid",
  "تحميل / عرض PDF": "Download / view PDF",
  "ادفع الفاتورة أونلاين": "Pay this invoice online",
  "ادفع الآن": "Pay now",
  "رمز المصادقة": "Auth code",
  "رقم الطلب": "Order no.",
  "مرجع البنك": "Bank ref.",
  "رقم المعاملة": "Transaction ID",
  // أنواع المطالبات — النصّ العربيّ نفسه المخزَّن في `gps_invoices.title` (fine-invoice.ts).
  "غرامة ضياع جهاز تتبّع": "Tracking device loss fine",
  "غرامة إتلاف جهاز تتبّع": "Tracking device damage fine",
  "أجرة تركيب جهاز": "Device installation fee",
  "أجرة نزع جهاز": "Device removal fee",
  "رسوم شريحة اتصال": "SIM connectivity fee",
  "رسوم أخرى": "Other charges",
  "الفوترة حسب أيام تعيين كل جهاز (الشهر = 30 يوماً). للاستفسار عن فاتورة تواصل مع المزوّد.": "Billing is by each device's assigned days (month = 30 days). For questions about an invoice, contact your provider.",
  // تبويبا صندوق الوارد (components/email-box.tsx) — كانا يظهران عربيّاً في الواجهة الإنجليزيّة
  "المحادثات": "Chats",
  "البريد": "Email",
  // نافذةُ الترحيب (إعداد تنبيهات الواتساب) — components/onboarding-dialog.tsx
  "مرحباً": "Welcome",
  "إعداد سريع لمرة وحدة عشان توصلك التنبيهات على واتساب": "A quick one-time setup so alerts reach you on WhatsApp",
  "رقم تلفونك (مع رمز الدولة)": "Your phone number (with country code)",
  "تنبيهات الواتساب (إشارة، تحويل محادثة، تأخّر...) بتوصلك على هذا الرقم.": "WhatsApp alerts (mentions, conversation transfers, delays…) will reach you on this number.",
  "التنبيهات اللي تبي تستقبلها على واتساب:": "Alerts you want to receive on WhatsApp:",
  "عند الإشارة لي في محادثة (منشن)": "When I'm mentioned in a conversation",
  "عند تحويل محادثة لي": "When a conversation is transferred to me",
  "تنبيهات التأخّر في الرد (SLA)": "Late-reply alerts (SLA)",
  "تقدر تغيّر رقمك وتفضيلات التنبيهات بأي وقت من صفحة": "You can change your number and alert preferences anytime from the",
  "حفظ وبدء الاستخدام": "Save and get started",
  "لاحقاً": "Later",
  // صفحة جهات الاتصال — pages/customers.tsx
  "أدخل أرقاماً أو ارفع ملفاً": "Enter numbers or upload a file",
  "لم يُعثر على أرقام صالحة": "No valid numbers found",
  "فشل الاستيراد": "Import failed",
  "استيراد جهات الاتصال": "Import contacts",
  "الصيغ المدعومة:": "Supported formats:",
  "رقم واحد في كل سطر:": "One number per line:",
  "اسم وفاصلة ورقم:": "Name, comma, number:",
  "أحمد محمد": "Ahmed Mohammed",
  "مع وسوم:": "With tags:",
  "أحمد": "Ahmed",
  "دائم": "Regular",
  "الصق الأرقام هنا": "Paste numbers here",
  "أو": "or",
  "رفع ملف CSV أو TXT": "Upload a CSV or TXT file",
  "انقر لاختيار الملف": "Click to choose a file",
  "{n} صالح": "{n} valid",
  "{n} غير صالح (سيُتجاهل)": "{n} invalid (will be skipped)",
  "الرقم": "Number",
  "الوسوم": "Tags",
  "رقم غير صالح": "Invalid number",
  "تم الاستيراد بنجاح!": "Import successful!",
  "أُضيف": "Added",
  "عميل": "customers",
  "تجاهل": "skipped",
  "مكرر": "duplicates",
  "معاينة البيانات": "Preview data",
  "استيراد {n} عميل": "Import {n} customers",
  "تم حفظ الاسم": "Name saved",
  "انقر للتعديل": "Click to edit",
  "تعذّر الدمج": "Merge failed",
  "تم دمج الصفَّين وحفظ الرقم": "Records merged and number saved",
  "تم حفظ الرقم": "Number saved",
  "الرقم مسجَّل لصفٍّ آخر": "Number belongs to another record",
  "يخصّ": "belongs to",
  "دمجُهما ينقل محادثاته وعقوده وحجوزاته إلى هذا الصفّ ثم يحذف الصفّ الآخر، ويحفظ الرقم هنا. لا يمكن التراجع.": "Merging moves its conversations, contracts and bookings to this record, deletes the other record, and saves the number here. This cannot be undone.",
  "جارٍ الدمج…": "Merging…",
  "ادمج الصفَّين": "Merge records",
  "بدون رقم": "No number",
  "رقم الهاتف مطلوب": "Phone number is required",
  "تم إضافة العميل": "Customer added",
  "فشل الإضافة — تحقق من الرقم": "Failed to add — check the number",
  "تم التحديث من Drive8: {fields}": "Updated from Drive8: {fields}",
  "لا توجد نواقص — البيانات محدّثة من Drive8": "Nothing missing — data is up to date from Drive8",
  "لا يوجد تطابق في Drive8 لهذا العميل": "No match in Drive8 for this customer",
  "تعذّر التحديث من Drive8": "Couldn't update from Drive8",
  "تم حفظ بيانات ERP": "ERP data saved",
  "ملف ERP": "ERP profile",
  "رقم العميل في ERP": "ERP customer no.",
  "آخر مزامنة": "Last sync",
  "جارٍ التحديث من Drive8…": "Updating from Drive8…",
  "بيانات إضافية (يدوية)": "Additional data (manual)",
  "رقم الرخصة": "License no.",
  "انتهاء الرخصة": "License expiry",
  "العنوان": "Address",
  "تحديث من Drive8": "Update from Drive8",
  "مطابقة مباشرة من ERP": "Live match from ERP",
  "تم العثور على العميل في ERP": "Customer found in ERP",
  "لا يوجد تطابق في ERP لهذا العميل.": "No match in ERP for this customer.",
  "العقد الجاري": "Current contract",
  "ملف التأجير (Drive8)": "Rental profile (Drive8)",
  "مستحق": "Due",
  "متبقّي": "Outstanding",
  "رصيد له": "Credit",
  "عقد نشط": "Active",
  "الرصيد": "Balance",
  "{n} له": "{n} credit",
  "بدء المزامنة…": "Starting sync…",
  "جارٍ… {done} / {total}": "Syncing… {done} / {total}",
  "تمت — {n} عميل": "Done — {n} customers",
  "اكتملت مزامنة العملاء من ERP": "Customer sync from ERP complete",
  "تعذّرت المزامنة": "Sync failed",
  "مزامنة Drive8…": "Syncing Drive8…",
  "Drive8… عملاء {customers} · عقود {contracts} · سيارات {cars}": "Drive8… customers {customers} · contracts {contracts} · cars {cars}",
  "تمت Drive8 — {customers} عميل / {contracts} عقد": "Drive8 done — {customers} customers / {contracts} contracts",
  "اكتملت مزامنة Drive8": "Drive8 sync complete",
  "تعذّرت مزامنة Drive8": "Drive8 sync failed",
  "مزامنة العقود…": "Syncing contracts…",
  "العقود… {n}": "Contracts… {n}",
  "تمت العقود — {n} عقد": "Contracts done — {n} contracts",
  "اكتملت مزامنة العقود": "Contract sync complete",
  "تعذّرت مزامنة العقود": "Contract sync failed",
  "تحديث النواقص من Drive8…": "Filling gaps from Drive8…",
  "تمت — أُثري {enriched} · أُنشئ {created}": "Done — enriched {enriched} · created {created}",
  "جارٍ تحديث النواقص من Drive8…": "Filling gaps from Drive8…",
  "اكتمل تحديث النواقص من Drive8": "Gap fill from Drive8 complete",
  "تعذّر تحديث النواقص": "Gap fill failed",
  "{n} نتيجة": "{n} results",
  "{n} عميل": "{n} customers",
  "يُعرض {n} — ابحث للوصول لأي عميل": "showing {n} — search to find any customer",
  "مزامنة من ERP": "Sync from ERP",
  "العقود الجديدة + الإغلاق فقط (سريعة) — تتم تلقائياً كل 5 دقائق": "New contracts + closures only (fast) — runs automatically every 5 minutes",
  "مزامنة العقود الآن": "Sync contracts now",
  "مزامنة كاملة لكل بيانات Drive8 — تتم تلقائياً كل ليلة": "Full sync of all Drive8 data — runs automatically every night",
  "مزامنة Drive8 كاملة": "Full Drive8 sync",
  "تعبئة نواقص العملاء من Drive8 (الرقم المدني/العنوان…) + إنشاء سجلات للعملاء الناقصين": "Fill customer gaps from Drive8 (civil ID/address…) + create records for missing customers",
  "تحديث النواقص من Drive8": "Fill gaps from Drive8",
  "استيراد": "Import",
  "إضافة عميل": "Add customer",
  "بحث بالاسم / الرقم المدني / رقم العقد / الهاتف...": "Search by name / civil ID / contract no. / phone...",
  "تاريخ الانضمام": "Joined",
  "لا يوجد عملاء بعد — ستُضاف تلقائياً عند أول رسالة واتساب": "No customers yet — they'll be added automatically on their first WhatsApp message",
  "عرض تفاصيل العقد": "View contract details",
  "{n} يوم متبقي": "{n} days left",
  "تأخر {n} يوم": "{n} days overdue",
  "الملف الشامل — كل شي عن العميل بصفحة وحدة": "360° profile — everything about the customer on one page",
  "فتح المحادثات": "Open conversations",
  "تاريخ الانتهاء": "End date",
  "سعر الإيجار الفعلي": "Actual rental price",
  "تاريخ الميلاد": "Date of birth",
  "رقم الجواز": "Passport no.",
  "الهاتف": "Phone",
  "هاتف إضافي": "Additional phone",
  "أجهزة فيها قراءات لم تُخزَّن اليوم — الجهاز لا يُبلَّغ ولا يعيد إرسالها، فهي غائبة عن الحساب.": "Devices with readings that were not stored today — the device is not notified and does not resend them, so they are missing from the calculation.",
  "الأرقام محجوبة — التخزين غير جاهز": "Figures hidden — storage is not ready",
  "لا تجاوز مرصود": "No out-of-zone activity detected",
  "لم تُرجَع بعد": "Not yet returned",
  "تاريخ الإرجاع الفعلي (من Drive8)": "Actual return date (from Drive8)",
  "لا تُراقَب": "Not monitored",
  "قراءة لم تُخزَّن": "Reading not stored",
  "قراءة بوقت جهاز غير صالح — مستبعدة من الحساب": "Reading with an invalid device timestamp — excluded from the calculation",
  "تعذّر حساب الخروج عن النطاق": "Could not calculate out-of-zone activity",
  "قراءة بوقت جهاز غير صالح — مستبعدة من الخط": "Reading with an invalid device timestamp — excluded from the route",
};

/** يترجم إن وُجدت ترجمة، وإلّا يعيد النصّ كما هو. */
export function translate(text: string, lang: Lang): string {
  if (lang === "ar") return text;
  return EN[text] ?? EN_PAGES[text] ?? text;
}

// ── ترجمة بلا خطّاف ─────────────────────────────────────────────────────────
// صفحةٌ واحدة (التتبّع) فيها ٤٨٠ نصّاً موزّعة على مكوّنات داخلية كثيرة، وإضافة
// `const { t } = useLang()` إلى كلٍّ منها تعديلٌ يدويّ في عشرات المواضع — وكل
// موضعٍ فرصةُ خطأ. فاللغة الجارية تُحفَظ هنا، و`tt()` تُستدعى من أي مكان: داخل
// مكوّن، أو في دالة مساعدة، أو في مصفوفة أعمدة خارج الشجرة.
//
// وإعادة الرسم مضمونة لأن `DirShell` يحمل `key={lang}`: تبديل اللغة يُعيد بناء
// الشجرة كلّها، فلا يبقى مكوّنٌ محفوظ (`memo`) على اللغة القديمة. والتبديل
// نادرٌ في العمر — مرّةً عند أوّل دخول — فثمنه لا يُذكر.
let currentLang_: Lang = "ar";

export function setCurrentLang(l: Lang): void { currentLang_ = l; }

/** اللغة الجارية — لتنسيق الأرقام والتواريخ، لا للنصوص. */
export function currentLang(): Lang { return currentLang_; }

/** ترجمة بلا خطّاف — انظر الشرح أعلاه. */
export function tt(text: string): string { return translate(text, currentLang_); }

/**
 * نصُّ الإشعار بلغة المستخدم. الإشعارُ يُنشأ في الخادم بنسختين (عربيّة +
 * `titleEn`/`bodyEn`) لأنّ فيه أرقاماً وأسماءً لا يترجمها قاموس؛ والقديمُ
 * بلا نسخةٍ إنجليزيّة يُعرض عربيّاً كما كُتب.
 */
export function notifText(n: { title: string; body: string; titleEn?: string | null; bodyEn?: string | null }): { title: string; body: string } {
  return currentLang_ === "en"
    ? { title: n.titleEn || n.title, body: n.bodyEn || n.body }
    : { title: n.title, body: n.body };
}

// الاتجاه واللغة المحليّة كانا مكتوبَين حرفيّاً في عشرات المواضع
// (`direction: "rtl"` و`toLocaleString("ar-KW")`)، فبقيت النوافذ مقلوبةً
// والتواريخ عربيّةً فوق واجهةٍ إنجليزية. الدالّتان تُغلقان باب التكرار: أيّ
// موضعٍ جديد يستدعيهما بدل أن يكتب القيمة من عنده.

/** اتجاه الكتابة كقيمة CSS — للحاويات التي تفرض اتجاهها بنفسها. */
export function dir(): "rtl" | "ltr" { return currentLang_ === "ar" ? "rtl" : "ltr"; }

/** اللغة المحليّة لتنسيق التواريخ والأوقات. */
export function locale(): string { return currentLang_ === "ar" ? "ar-KW" : "en-GB"; }

/** يضبط اتجاه المستند ولغته. يُستدعى عند الإقلاع وعند كل تبديل. */
export function applyDocumentLang(lang: Lang): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.lang = lang;
  el.dir = lang === "ar" ? "rtl" : "ltr";
}

export const LANG_STORAGE_KEY = "jisr_lang";

export function readStoredLang(): Lang | null {
  try {
    const v = localStorage.getItem(LANG_STORAGE_KEY);
    return v === "ar" || v === "en" ? v : null;
  } catch { return null; }
}

// الصفحاتُ العامّة (الشروط، رابط الرحلة) تُرسَم قبل `LangProvider`، فلا يضبط
// أحدٌ اللغة لها — فتبدأ اللغةُ الجارية من الاختيار المحفوظ لا من العربية دائماً.
setCurrentLang(readStoredLang() ?? "ar");
